import { useState, useEffect, useRef, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import VesselResolver from './VesselResolver';
import { Shuffle, Info, ExternalLink, X, Package, ShieldCheck, Lock, CheckCircle2, Send, Ship, Radio } from 'lucide-react';
import { api, fileUrl } from '../../lib/api';
import { useAiEnabled, aiDocExtract, fileToDataUrl, type AiDocExtract } from '../../lib/procurementAi';
import { FulfilmentSummary, BatchList, useFulfilment } from './FulfilmentSummary';

/**
 * Action modal toolkit for the Indent Detail page.
 *
 * One module exporting a small modal per write endpoint so the
 * IndentDetail page stays a list of clear, named actions. Every modal
 * follows the same shape:
 *   • props: { indentId, onClose, refresh }
 *   • internal local state + a single useMutation
 *   • toast + close on success; toast on error
 *
 * Keeps the IndentDetail file focused on layout — actual write logic
 * lives here so each handler is one screen + 30 lines of JSX.
 */

interface ModalShellProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  busy?: boolean;
  onSubmit: () => void;
  submitLabel?: string;
  submitDisabled?: boolean;
  width?: 'narrow' | 'wide';
}

function ModalShell({ title, onClose, children, busy, onSubmit, submitLabel = 'Save', submitDisabled, width = 'narrow' }: ModalShellProps) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-3 sm:p-4">
      <div className={`bg-white rounded-2xl shadow-2xl ${width === 'wide' ? 'max-w-3xl' : 'max-w-md'} w-full max-h-[90vh] flex flex-col`}>
        <div className="px-3 py-3 sm:px-5 border-b border-slate-200 flex items-center justify-between gap-2">
          <div className="text-sm font-bold text-slate-900">{title}</div>
          <button className="text-slate-400 hover:text-slate-700 text-2xl leading-none shrink-0" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="px-3 py-4 sm:px-5 overflow-y-auto flex-1 space-y-3">{children}</div>
        <div className="px-3 py-3 sm:px-5 border-t border-slate-200 flex flex-wrap justify-end gap-2">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button
            className="btn-primary"
            disabled={busy || submitDisabled}
            onClick={onSubmit}
          >
            {busy ? 'Working…' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ActionProps {
  indentId: string;
  onClose: () => void;
}

// ── RFQ broadcast ──────────────────────────────────────────────────────

type RateHistoryEntry = {
  vendorName: string; vendorKey: string; rate: number; currency: string; yom: string;
  indentId: string; at: string | null; productCode: string; itemType: string; grade: string;
  match: 'exact' | 'similar'; priorAwards: number;
};
type RateHistoryResp = {
  basis: { indentId: string; indentType: string; productCode: string; itemType: string; grade: string };
  byVendor: Record<string, RateHistoryEntry>;
  scanned: number;
};
type DeliveryStatus = 'sent' | 'skipped' | 'failed' | 'no_email';
type BroadcastResp = {
  broadcasts: Array<{ vendorId: string; vendorName: string; email: string; delivery: { status: DeliveryStatus; error?: string } }>;
  delivery: { sent: number; skipped: number; failed: number; noEmail: number; total: number };
};

/** Same normaliser as the server's `vendorNameKey`, so the rate-history map lines up. */
const vendorKeyOf = (name: string) => String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');

const money = (n: number, ccy: string) =>
  `${ccy === 'INR' ? '₹' : `${ccy} `}${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

/**
 * What we last paid this vendor for a comparable box.
 *
 * Rendered inline on the vendor row rather than behind a hover, because the
 * whole point is that the choice of who to invite gets made next to the price
 * — a tooltip would mean opening 149 of them. `similar` is labelled distinctly
 * from `exact` so a same-grade-different-code price is never read as the same
 * thing (see services/procurement/vendor-rate-history.ts).
 */
function RateHistoryTag({ h }: { h?: RateHistoryEntry }) {
  if (!h) return null;
  const exact = h.match === 'exact';
  return (
    <span
      className={
        'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ' +
        (exact ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
               : 'bg-amber-50 text-amber-700 border border-amber-200')
      }
      title={
        `Last awarded ${money(h.rate, h.currency)}${h.yom ? ` for a ${h.yom} unit` : ''}`
        + ` on indent ${h.indentId}${h.at ? ` (${h.at.slice(0, 10)})` : ''}`
        + `\nMatched ${exact ? `exactly on product code ${h.productCode}` : `on ${h.itemType} + ${h.grade}, not the exact product code`}`
        + `\n${h.priorAwards} awarded line${h.priorAwards === 1 ? '' : 's'} from this vendor`
      }
    >
      {money(h.rate, h.currency)}
      <span className="font-normal opacity-70">· YOM {h.yom || '—'}</span>
      {!exact && <span className="font-normal opacity-70">· similar</span>}
    </span>
  );
}

/**
 * Whether the last RFQ mail to this vendor actually left the server.
 *
 * `unknown` renders as nothing rather than as a tick: those invites were
 * recorded when the send was fire-and-forget, so there is no evidence either
 * way and a green badge would be inventing one.
 */
function DeliveryBadge({ status, error }: { status?: string; error?: string }) {
  if (!status || status === 'unknown') return null;
  const map: Record<string, { label: string; cls: string }> = {
    sent: { label: 'Delivered to mail server', cls: 'text-emerald-700' },
    skipped: { label: 'Not sent — mail not configured', cls: 'text-amber-700' },
    failed: { label: 'Send failed', cls: 'text-rose-700' },
    no_email: { label: 'No email on file', cls: 'text-rose-700' },
  };
  const m = map[status];
  if (!m) return null;
  return (
    <div className={`text-[10px] font-semibold ${m.cls}`} title={error || undefined}>
      {status === 'sent' ? '✓' : '✗'} {m.label}
    </div>
  );
}

/** Per-vendor outcome of the send, shown after the broadcast returns. */
function DeliveryRow({ name, email, status, error }: { name: string; email: string; status: DeliveryStatus; error?: string }) {
  const tone = status === 'sent' ? 'text-emerald-700' : status === 'skipped' ? 'text-amber-700' : 'text-rose-700';
  const label = status === 'sent' ? 'Sent' : status === 'skipped' ? 'Not sent' : status === 'no_email' ? 'No email' : 'Failed';
  return (
    <li className="flex items-start gap-2 px-2 py-1 border-b border-slate-100 last:border-0 text-[11.5px]">
      <span className={`font-bold shrink-0 ${tone}`}>{status === 'sent' ? '✓' : '✗'}</span>
      <div className="min-w-0 flex-1">
        <span className="font-medium text-slate-800">{name}</span>
        {email && <span className="text-slate-400"> · {email}</span>}
        {error && <div className={`${tone} text-[10.5px]`}>{error}</div>}
      </div>
      <span className={`shrink-0 font-semibold ${tone}`}>{label}</span>
    </li>
  );
}

export function RfqBroadcastModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [customMessage, setCustomMessage] = useState('');
  const [q, setQ] = useState('');
  const [matchRegionOnly, setMatchRegionOnly] = useState(true);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [result, setResult] = useState<BroadcastResp | null>(null);

  const { data, isLoading } = useQuery<{ vendors: any[] }>({
    queryKey: ['procurement', 'vendors', 'active'],
    queryFn: () => api.get('/api/procurement/vendors?active=true'),
  });
  const vendors = data?.vendors || [];

  // What we've paid these vendors before for this spec. Never blocks the
  // picker — no history is the normal case for most of a 149-vendor master.
  const { data: rates } = useQuery<RateHistoryResp>({
    queryKey: ['procurement', 'rate-history', indentId],
    queryFn: () => api.get(`/api/procurement/rfq/rate-history/${encodeURIComponent(indentId)}`),
    enabled: !!indentId,
    staleTime: 5 * 60_000,
  });
  const history = rates?.byVendor || {};
  const indentType = rates?.basis.indentType || '';

  /**
   * The legacy portal filtered this list hard on vendor region — an Import
   * indent showed only Import vendors. Ported as a DEFAULT rather than a law,
   * because our vendor master has a region on 1 of 149 rows: the strict version
   * would show an empty picker and the RFQ could not be sent at all. So an
   * unclassified vendor stays visible (there is no evidence they're wrong for
   * this indent, only missing data), and the filter tightens on its own as
   * vendors get classified. The toggle shows what it's hiding either way.
   */
  const regionMatched = useMemo(() => {
    const want = indentType.trim().toLowerCase();
    if (!matchRegionOnly || !want) return vendors;
    return vendors.filter((v) => {
      const region = String(v.region || '').trim().toLowerCase();
      return !region || region === want || region === 'both';
    });
  }, [vendors, indentType, matchRegionOnly]);
  const hiddenByRegion = vendors.length - regionMatched.length;

  /**
   * Filter on name, email, phone and category — the four things someone
   * actually knows about the vendor they are hunting for.
   *
   * Anyone already ticked stays visible whatever the search says: otherwise a
   * second search silently hides the first pick and the count at the bottom
   * stops matching what is on screen.
   */
  const shownVendors = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return regionMatched;
    return regionMatched.filter((v) => selected.includes(v._id)
      || `${v.name || ''} ${v.email || ''} ${v.phone || ''} ${v.category || ''}`.toLowerCase().includes(needle));
  }, [regionMatched, q, selected]);

  /**
   * Grouped by category with a whole-category toggle, matching the reference's
   * accordion. Vendors we have bought this spec from before sort to the top of
   * their category — they are the ones a purchaser wants to invite first, and
   * hunting for them alphabetically among 33 "Container" vendors is the exact
   * chore this screen exists to remove.
   */
  const groups = useMemo(() => {
    const by = new Map<string, any[]>();
    for (const v of shownVendors) {
      const cat = String(v.category || '').trim() || 'Uncategorised';
      if (!by.has(cat)) by.set(cat, []);
      by.get(cat)!.push(v);
    }
    for (const list of by.values()) {
      list.sort((a, b) => {
        const ah = history[vendorKeyOf(a.name)] ? 0 : 1;
        const bh = history[vendorKeyOf(b.name)] ? 0 : 1;
        return ah - bh || String(a.name || '').localeCompare(String(b.name || ''));
      });
    }
    return Array.from(by.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [shownVendors, history]);

  const emailable = (v: any) => !!String(v.email || '').trim();
  const toggleOne = (id: string) =>
    setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  /** Whole-category switch. Vendors with no email are skipped — ticking one
   *  would put a name on the send list that can never receive anything. */
  const toggleCategory = (list: any[], on: boolean) => {
    const ids = list.filter(emailable).map((v) => v._id);
    setSelected((p) => (on ? Array.from(new Set([...p, ...ids])) : p.filter((x) => !ids.includes(x))));
  };

  const submit = useMutation({
    mutationFn: () => api.post<BroadcastResp>('/api/procurement/rfq/broadcast', {
      indentId,
      vendorIds: selected,
      customMessage,
    }),
    onSuccess: (r) => {
      setResult(r);
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      qc.invalidateQueries({ queryKey: ['procurement', 'rfq-invites', indentId] });
      const d = r.delivery;
      // Say what the mail server actually did. Reporting the number PICKED as
      // the number sent is how an unconfigured mailer used to look like success.
      if (d.sent === d.total) toast.success(`RFQ emailed to ${d.sent} vendor(s)`);
      else if (d.sent) toast(`${d.sent} of ${d.total} RFQ emails sent — see the delivery report`, { icon: '⚠️' });
      else toast.error('No RFQ emails went out — see the delivery report');
    },
    onError: (e: any) => toast.error(e?.message || 'Broadcast failed'),
  });

  // ── Delivery report ──────────────────────────────────────────────────
  if (result) {
    const d = result.delivery;
    const bad = result.broadcasts.filter((b) => b.delivery.status !== 'sent');
    return (
      <ModalShell title="RFQ delivery report" onClose={onClose} onSubmit={onClose} submitLabel="Done" width="wide">
        <div
          className={
            'rounded-lg border px-3 py-2 text-sm ' +
            (d.sent === d.total ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : d.sent ? 'border-amber-200 bg-amber-50 text-amber-900'
              : 'border-rose-200 bg-rose-50 text-rose-800')
          }
        >
          <div className="font-bold">{d.sent} of {d.total} RFQ emails sent</div>
          <div className="mt-0.5 text-[11.5px]">
            {d.failed > 0 && <>{d.failed} failed. </>}
            {d.skipped > 0 && <>{d.skipped} not sent because mail isn&rsquo;t configured on this server. </>}
            {d.noEmail > 0 && <>{d.noEmail} had no email address on file. </>}
            {d.sent === d.total
              ? 'The quotation links are live and the round is open.'
              : 'Every vendor still has a quotation link recorded — use Resend from Live quotes once the address or mail setup is fixed.'}
          </div>
        </div>

        {!!bad.length && (
          <div className="rounded-lg border border-slate-200">
            <div className="border-b border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-500">
              Needs attention
            </div>
            <ul>
              {bad.map((b) => (
                <DeliveryRow key={b.vendorId} name={b.vendorName} email={b.email} status={b.delivery.status} error={b.delivery.error} />
              ))}
            </ul>
          </div>
        )}

        <details className="rounded-lg border border-slate-200">
          <summary className="cursor-pointer px-2 py-1 text-[11px] font-semibold text-slate-600">
            All {result.broadcasts.length} recipients
          </summary>
          <ul>
            {result.broadcasts.map((b) => (
              <DeliveryRow key={b.vendorId} name={b.vendorName} email={b.email} status={b.delivery.status} error={b.delivery.error} />
            ))}
          </ul>
        </details>
      </ModalShell>
    );
  }

  // ── Vendor picker ────────────────────────────────────────────────────
  return (
    <ModalShell
      title="Broadcast RFQ"
      onClose={onClose}
      onSubmit={() => submit.mutate()}
      busy={submit.isPending}
      submitLabel={`Send to ${selected.length} vendor(s)`}
      submitDisabled={!selected.length}
      width="wide"
    >
      <div className="text-xs text-slate-500">
        Pick the vendors who should receive a secure quotation portal link.
        {rates?.basis.productCode && (
          <> Prices shown are what we last paid for <b>{rates.basis.productCode}</b>
            {rates.basis.grade ? ` (${rates.basis.grade})` : ''}.</>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          className="input flex-1 min-w-[200px]"
          placeholder="Search vendors by name, email, phone or category…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
        />
        {!!selected.length && (
          <button
            type="button"
            className="text-[11px] font-bold text-slate-500 hover:text-slate-800 underline"
            onClick={() => setSelected([])}
          >
            Clear {selected.length} selected
          </button>
        )}
      </div>

      {!!indentType && (
        <label className="flex items-center gap-2 text-[11px] text-slate-600">
          <input type="checkbox" checked={matchRegionOnly} onChange={(e) => setMatchRegionOnly(e.target.checked)} />
          <span>
            Only <b>{indentType}</b> vendors
            {hiddenByRegion > 0
              ? <span className="text-slate-400"> · hiding {hiddenByRegion}</span>
              : <span className="text-slate-400"> · nothing hidden (vendors without a region tag always show)</span>}
          </span>
        </label>
      )}

      {isLoading ? <div className="text-xs text-slate-400">Loading vendors…</div> : (
        <div className="border border-slate-200 rounded-lg max-h-80 overflow-y-auto">
          {vendors.length === 0 && <div className="p-4 text-xs text-slate-400 text-center">No active vendors. Add some from the Vendors page first.</div>}
          {!!vendors.length && !shownVendors.length && (
            <div className="p-4 text-xs text-slate-400 text-center">No vendor matches &ldquo;{q}&rdquo;.</div>
          )}
          {groups.map(([cat, list]) => {
            const ids = list.filter(emailable).map((v) => v._id);
            const allOn = ids.length > 0 && ids.every((id) => selected.includes(id));
            const isShut = collapsed[cat];
            const withHistory = list.filter((v) => history[vendorKeyOf(v.name)]).length;
            return (
              <div key={cat}>
                <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-2 py-1.5">
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-700 text-[10px] w-3"
                    onClick={() => setCollapsed((p) => ({ ...p, [cat]: !p[cat] }))}
                    aria-label={isShut ? `Expand ${cat}` : `Collapse ${cat}`}
                  >
                    {isShut ? '▶' : '▼'}
                  </button>
                  <span className="text-[11px] font-bold text-slate-700 flex-1 truncate">{cat}</span>
                  {withHistory > 0 && (
                    <span className="text-[10px] text-emerald-700">{withHistory} bought before</span>
                  )}
                  <span className="text-[10px] text-slate-400">{list.length}</span>
                  <label className="flex items-center gap-1 text-[10px] text-slate-500" title={`Select every ${cat} vendor with an email`}>
                    <input
                      type="checkbox"
                      checked={allOn}
                      disabled={!ids.length}
                      onChange={(e) => toggleCategory(list, e.target.checked)}
                    />
                    All
                  </label>
                </div>
                {!isShut && list.map((v) => {
                  const on = selected.includes(v._id);
                  const canMail = emailable(v);
                  const h = history[vendorKeyOf(v.name)];
                  return (
                    <label
                      key={v._id}
                      className={
                        'flex items-center gap-3 px-3 py-2 border-b border-slate-100 text-sm '
                        + (!canMail ? 'bg-slate-50 cursor-not-allowed' : on ? 'bg-teal-50 cursor-pointer' : 'hover:bg-slate-50 cursor-pointer')
                      }
                      title={canMail ? undefined : 'No email address on file — add one on the Vendors page to invite them'}
                    >
                      <input type="checkbox" checked={on} disabled={!canMail} onChange={() => toggleOne(v._id)} />
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium truncate ${canMail ? 'text-slate-800' : 'text-slate-400'}`}>{v.name}</div>
                        <div className="text-[11px] truncate">
                          {canMail
                            ? <span className="text-slate-500">{v.email} · {v.phone || 'no phone'}</span>
                            : <span className="text-rose-600 font-semibold">No email on file</span>}
                        </div>
                      </div>
                      <RateHistoryTag h={h} />
                    </label>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {!!vendors.length && (
        <div className="text-[11px] text-slate-400">
          Showing {shownVendors.length} of {vendors.length} vendors · {selected.length} selected
          {rates && rates.scanned > 0 && <> · prices from {rates.scanned} past awarded line(s)</>}
        </div>
      )}

      <div>
        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Custom message (optional)</label>
        <textarea className="input mt-1 min-h-[60px]" value={customMessage} onChange={(e) => setCustomMessage(e.target.value)} />
      </div>
    </ModalShell>
  );
}

// ── Compare + Award ────────────────────────────────────────────────────
export function CompareAwardModal({ indentId, onClose, items: itemsProp, indentQty }: ActionProps & { items: any[]; indentQty: number }) {
  const qc = useQueryClient();
  // Map of itemId → awardedQty (0 = not awarded)
  const [draft, setDraft] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    itemsProp.forEach((it) => { init[it.itemId] = it.awardedQty || 0; });
    return init;
  });
  const set = (id: string, q: number) => setDraft((p) => ({ ...p, [id]: Math.max(0, q) }));
  const total = Object.values(draft).reduce((s, n) => s + (Number(n) || 0), 0);
  const pending = Math.max(0, indentQty - total);
  const over = Math.max(0, total - indentQty);
  // Ordered vs awarded vs still to place, from the same server computation the
  // rest of the app reads.
  const { data: fulfilment } = useFulfilment(indentId);

  const submit = useMutation({
    mutationFn: () => api.post('/api/procurement/awards/finalize', {
      indentId,
      awards: Object.entries(draft).filter(([, q]) => q > 0).map(([itemId, awardedQty]) => ({ itemId, awardedQty })),
    }),
    onSuccess: () => {
      toast.success('Vendors finalised');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'Award failed'),
  });

  return (
    <ModalShell
      title="Compare quotes & award"
      onClose={onClose}
      onSubmit={() => submit.mutate()}
      busy={submit.isPending}
      submitLabel={over > 0 ? `${over} over the order` : pending > 0 ? `Award ${total} of ${indentQty}` : 'Confirm awards'}
      submitDisabled={total === 0 || over > 0}
      width="wide"
    >
      <div className="text-xs text-slate-500">
        Allocate quantity to one or more vendors. Award fewer than {indentQty} for a partial award — the balance
        stays open and can be placed with another vendor later.
      </div>
      <FulfilmentSummary f={fulfilment} stage="award" />
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Vendor</th>
              <th className="text-right px-3 py-2 font-semibold">Rate</th>
              <th className="text-left px-3 py-2 font-semibold">Delivery</th>
              <th className="text-left px-3 py-2 font-semibold">Payment</th>
              <th className="text-right px-3 py-2 font-semibold">Award qty</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {itemsProp.length === 0 && (
              <tr><td colSpan={5} className="text-center py-6 text-slate-400 text-xs">No quotes yet. Broadcast an RFQ first.</td></tr>
            )}
            {itemsProp.map((it) => (
              <tr key={it.itemId} className="hover:bg-slate-50/60">
                <td className="px-3 py-2 font-medium">{it.vendorName}</td>
                <td className="px-3 py-2 text-right font-mono text-sm">{it.currency} {Number(it.rate || 0).toLocaleString('en-IN')}</td>
                <td className="px-3 py-2 text-[11px] text-slate-600">{it.deliveryTime || '—'}</td>
                <td className="px-3 py-2 text-[11px] text-slate-600">{it.paymentTerms || '—'}</td>
                <td className="px-3 py-2 text-right">
                  <input
                    type="number"
                    min={0}
                    className="w-20 text-right input"
                    value={draft[it.itemId] ?? 0}
                    onChange={(e) => set(it.itemId, Number(e.target.value) || 0)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            {/* Live total, coloured like the reference decision matrix: green
                when it lands exactly on the order, amber for a partial award,
                red when it overshoots. */}
            <tr className="bg-slate-50 border-t border-slate-200">
              <td colSpan={4} className="px-3 py-2 text-right text-xs uppercase text-slate-500 font-bold">Total allocated</td>
              <td className={
                'px-3 py-2 text-right font-bold '
                + (over > 0 ? 'text-rose-700' : pending === 0 && total > 0 ? 'text-emerald-700' : 'text-amber-700')
              }>
                {total} / {indentQty}
              </td>
            </tr>
            <tr className="bg-slate-50">
              <td colSpan={4} className="px-3 py-1 text-right text-[11px] text-slate-500">
                {over > 0 ? 'Over the order quantity' : pending > 0 ? 'Still to award' : 'Fully awarded'}
              </td>
              <td className={
                'px-3 py-1 text-right text-[11px] font-bold '
                + (over > 0 ? 'text-rose-700' : pending > 0 ? 'text-amber-700' : 'text-emerald-700')
              }>
                {over > 0 ? `+${over}` : pending}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </ModalShell>
  );
}

// ── LOI dispatch ───────────────────────────────────────────────────────
//
// Pre-fills the entire provisional-order body the way the legacy Apps
// Script did — vendor name, indent id, qty × item code, MGW limits,
// CSC plate, USD rate, depot delivery. Operator can still tweak before
// dispatch but they don't have to start from a blank box.
function buildLoiBody(opts: {
  vendorName: string;
  indentId: string;
  quantity: number;
  productCode?: string;
  itemType?: string;
  sizeAllDetails?: string;
  awardedRate?: number;
  awardedCurrency?: string;
  deliveryLocation?: string;
  dateOfDelivery?: string;
  paymentTerms?: string;
}): string {
  const lines = [
    `Dear ${opts.vendorName || 'Vendor'},`,
    '',
    'Greetings from Crystal Group.',
    '',
    `We are pleased to confirm our intent to procure the following against Indent ${opts.indentId}:`,
    '',
    `  • Quantity      : ${opts.quantity} × ${opts.productCode || opts.itemType || 'container'}${opts.sizeAllDetails ? ` (${opts.sizeAllDetails})` : ''}`,
    `  • Awarded Rate  : ${opts.awardedCurrency || 'USD'} ${Number(opts.awardedRate || 0).toLocaleString('en-IN')} per unit`,
    `  • Delivery To   : ${opts.deliveryLocation || 'Depot — to be advised'}`,
    `  • Target Date   : ${opts.dateOfDelivery || 'As per signed sales contract'}`,
    `  • Payment Terms : ${opts.paymentTerms || '30% advance · 70% balance against BL copy'}`,
    '',
    'Manufacturing & quality requirements:',
    '  • 6mm MS checker-plate flooring throughout, free of pitting',
    '  • Maximum Gross Weight (MGW) compliant — 30,480 kg per CSC plate',
    '  • Valid CSC plate fitted to each unit before dispatch',
    '  • Container marking, paint, decals as per our PO drawing',
    '',
    'Please treat this LOI as authorisation to commence production. Kindly courier the signed sales contract and the proforma invoice at the earliest so we can release the advance.',
    '',
    'Looking forward to a smooth shipment.',
    '',
    'Regards,',
    'Crystal Group Procurement Desk',
  ];
  return lines.join('\n');
}

export function LoiModal({
  indentId,
  onClose,
  awardedVendor,
  awardedVendorEmail,
  indent,
}: ActionProps & {
  awardedVendor: string;
  awardedVendorEmail?: string;
  indent?: any;
}) {
  const qc = useQueryClient();
  const [vendorEmail, setVendorEmail] = useState(awardedVendorEmail || indent?.awardedVendorEmail || '');
  const [subject, setSubject] = useState('Provisional Order Award - Crystal Group');
  const [body, setBody] = useState(() =>
    buildLoiBody({
      vendorName: awardedVendor,
      indentId,
      quantity: indent?.quantity || 1,
      productCode: indent?.productCode,
      itemType: indent?.itemType,
      sizeAllDetails: indent?.sizeAllDetails,
      awardedRate: indent?.awardedRate,
      awardedCurrency: indent?.awardedCurrency,
      deliveryLocation: indent?.deliveryLocation,
      dateOfDelivery: indent?.dateOfDelivery,
      paymentTerms: indent?.paymentTerms,
    }),
  );

  const submit = useMutation({
    mutationFn: () => api.post('/api/procurement/awards/loi', { indentId, vendorEmail, subject, body }),
    onSuccess: () => {
      toast.success('LOI dispatched');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'LOI failed'),
  });

  return (
    <ModalShell
      title="Dispatch LOI"
      onClose={onClose}
      onSubmit={() => submit.mutate()}
      busy={submit.isPending}
      submitLabel="Send LOI"
      submitDisabled={!vendorEmail || !subject}
      width="wide"
    >
      <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-[11px] text-teal-800">
        The body below is pre-filled from indent specs (qty, code, rate, delivery, payment terms). Edit before sending if needed — the vendor will see exactly what you ship.
      </div>
      <Field label="Vendor email" required>
        <input className="input" value={vendorEmail} onChange={(e) => setVendorEmail(e.target.value)} />
      </Field>
      <Field label="Subject" required>
        <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} />
      </Field>
      <Field label="Body">
        <textarea className="input min-h-[320px] font-mono text-[12px] leading-relaxed" value={body} onChange={(e) => setBody(e.target.value)} />
      </Field>
    </ModalShell>
  );
}

// ── Live Quotes view (real-time vendor quote feed) ─────────────────────
//
// Read-only window into the RFQ pipeline — every vendor that received
// the broadcast, whether they've responded, the rate / terms they sent
// back. Refetches every 15s so the desk can leave it open while vendors
// reply through the magic-link portal.
/** "3 days ago" — the thing you actually want to know before chasing again. */
function sinceLabel(v: string | Date): string {
  const t = new Date(v).getTime();
  if (!Number.isFinite(t)) return '—';
  const mins = Math.floor((Date.now() - t) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export function LiveQuotesModal({ indentId, onClose }: ActionProps) {
  const { data, isFetching, refetch } = useQuery<{ items: any[]; broadcasts: any[] }>({
    queryKey: ['procurement', 'indent', indentId, 'live-quotes'],
    queryFn: () => api.get(`/api/procurement/indents/${encodeURIComponent(indentId)}/live-quotes`),
    refetchInterval: 15_000,
  });
  const items = data?.items || [];
  const broadcasts = data?.broadcasts || [];

  // Index quotes by vendor email so we can render one row per vendor that
  // was broadcast to, with quoted/pending state inline.
  const quoteByVendor = new Map<string, any>();
  for (const it of items) {
    const k = (it.vendorEmail || it.vendorName || '').toLowerCase();
    if (k) quoteByVendor.set(k, it);
  }
  const rows = broadcasts.length
    ? broadcasts.map((b) => ({
        vendorId: b.vendorId || '',
        vendorName: b.vendorName,
        vendorEmail: b.vendorEmail,
        sentAt: b.sentAt,
        lastSentAt: b.lastSentAt || b.sentAt,
        sendCount: b.sendCount || 1,
        deliveryStatus: String(b.deliveryStatus || 'unknown'),
        deliveryError: String(b.deliveryError || ''),
        quote: quoteByVendor.get((b.vendorEmail || '').toLowerCase()) || null,
      }))
    : items.map((q) => ({ vendorId: '', vendorName: q.vendorName, vendorEmail: q.vendorEmail, sentAt: null, lastSentAt: null, sendCount: 0, deliveryStatus: 'unknown', deliveryError: '', quote: q }));
  const responded = rows.filter((r) => !!r.quote).length;

  // Chasing a silent vendor is the commonest action here; without this the only
  // option was re-broadcasting to everyone.
  const resend = useMutation({
    mutationFn: (v: { vendorId: string; vendorName: string }) =>
      api.post<{ vendorName: string; sentTo: string; delivery?: { status: string; error?: string } }>('/api/procurement/rfq/resend', {
        indentId, vendorId: v.vendorId, vendorName: v.vendorName,
      }),
    onSuccess: (r) => {
      // The server now says whether the mail actually left. A resend exists
      // because a vendor went quiet, so a green toast over a silent no-op is
      // the worst possible answer here.
      if (r.delivery?.status === 'sent') toast.success(`RFQ re-sent to ${r.vendorName}${r.sentTo ? ` · ${r.sentTo}` : ''}`);
      else toast.error(`Not sent to ${r.vendorName} — ${r.delivery?.error || 'the mail did not go out'}`);
      refetch();
    },
    onError: (e: any) => toast.error(e?.message || 'Resend failed'),
  });
  const [resending, setResending] = useState('');

  return (
    <ModalShell
      title="Live quotes feed"
      onClose={onClose}
      onSubmit={() => refetch()}
      submitLabel={isFetching ? 'Refreshing…' : 'Refresh now'}
      busy={isFetching}
      width="wide"
    >
      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>{responded} / {rows.length} vendors have responded</span>
        <span className="text-[10px] uppercase tracking-wider text-teal-600">Auto-refresh · 15s</span>
      </div>
      <div className="overflow-x-auto border border-slate-200 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Vendor</th>
              <th className="text-right px-3 py-2 font-semibold">Rate</th>
              <th className="text-right px-3 py-2 font-semibold">Qty</th>
              <th className="text-left px-3 py-2 font-semibold">Delivery</th>
              <th className="text-left px-3 py-2 font-semibold">Payment</th>
              <th className="text-left px-3 py-2 font-semibold">Status</th>
              <th className="text-left px-3 py-2 font-semibold">Last sent</th>
              <th className="text-right px-3 py-2 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-slate-400 text-xs">No RFQ broadcast yet.</td></tr>
            )}
            {rows.map((r, idx) => (
              <tr key={`${r.vendorEmail || r.vendorName}-${idx}`} className={r.quote ? 'hover:bg-emerald-50/40' : 'hover:bg-slate-50/60'}>
                <td className="px-3 py-2">
                  <div className="font-medium text-slate-800">{r.vendorName || '—'}</div>
                  <div className="text-[10px] text-slate-400 truncate max-w-[180px]">{r.vendorEmail || ''}</div>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">
                  {r.quote ? `${r.quote.currency || ''} ${Number(r.quote.rate || 0).toLocaleString('en-IN')}` : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">{r.quote?.quotedQty ?? '—'}</td>
                <td className="px-3 py-2 text-[11px] text-slate-600">{r.quote?.deliveryTime || '—'}</td>
                <td className="px-3 py-2 text-[11px] text-slate-600">{r.quote?.paymentTerms || '—'}</td>
                <td className="px-3 py-2">
                  {r.quote
                    ? <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">Quoted</span>
                    : <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Pending</span>}
                </td>
                <td className="px-3 py-2 text-[11px] text-slate-600">
                  {r.lastSentAt ? (
                    <>
                      <div>{sinceLabel(r.lastSentAt)}</div>
                      <div className="text-[10px] text-slate-400">
                        {new Date(r.lastSentAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        {r.sendCount > 1 ? ` · sent ${r.sendCount}×` : ''}
                      </div>
                      <DeliveryBadge status={r.deliveryStatus} error={r.deliveryError} />
                    </>
                  ) : <span className="text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-right">
                  <button
                    type="button"
                    disabled={resend.isPending && resending === r.vendorName}
                    onClick={() => { setResending(r.vendorName); resend.mutate({ vendorId: r.vendorId, vendorName: r.vendorName }); }}
                    className="press rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    title="Send this vendor the RFQ again"
                  >
                    {resend.isPending && resending === r.vendorName ? 'Sending…' : 'Resend'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ModalShell>
  );
}

// ── Override Status (god-mode) ─────────────────────────────────────────
//
// Bypasses normal phase-gating and force-pushes the indent to any
// pipeline status. Requires the operator to re-enter their password,
// captures a mandatory reason, and the backend writes the change to
// statusHistory tagged with kind='override' for the audit log.
// Exact replica of the reference `openOverrideModal` phase-grouped option lists
// (index.html). { v } = the status value; label defaults to the value but a few
// reference options show a different display label.
type OvOpt = { v: string; label?: string };
type OvGroup = { group: string; values: OvOpt[] };

const IMPORT_OVERRIDE_STAGES: OvGroup[] = [
  {
    group: 'Phase 1: Sourcing & Contracting',
    values: [
      { v: 'Indent Created' }, { v: 'RFQ in Progress' }, { v: 'RFQ Completed' },
      { v: 'Partially Awarded' }, { v: 'Vendor Finalized' },
      { v: 'Awaiting Sales Contract' }, { v: 'Contract Signed' },
    ],
  },
  {
    group: 'Phase 2: Finance & Approvals',
    values: [
      { v: 'Pending Advance Payment' }, { v: 'Advance Requested - Ready for Shipment' },
      { v: 'Paid - Ready for Shipment' },
    ],
  },
  {
    group: 'Phase 3: Manufacturing & Dispatch',
    values: [
      { v: 'Manufacturing in Progress' }, { v: 'Manufacturing Complete' },
      { v: 'Vessel Assigned - Awaiting Departure' },
      { v: 'Awaiting SIMS Payment' }, { v: 'SIMS Completed - Ready for Port' },
    ],
  },
  {
    group: 'Phase 4: Transit & Depot',
    values: [
      { v: 'In Transit - On Water' }, { v: 'Arrived at Port' }, { v: 'In CFS' },
      { v: 'Customs Cleared' }, { v: 'Arrived at Depot' },
      { v: 'Awaiting Depot Verification' }, { v: 'Depot Issue Reported' },
      { v: 'Pipeline Complete' },
    ],
  },
];

const DOMESTIC_OVERRIDE_STAGES: OvGroup[] = [
  {
    group: 'Phase 1: Sourcing & Contracting',
    values: [
      { v: 'Indent Created' }, { v: 'RFQ in Progress' }, { v: 'RFQ Completed' },
      { v: 'Partially Awarded' }, { v: 'Vendor Finalized' }, { v: 'Awaiting 2.1 Submission' },
      // Force-selecting this unblocks the same "Generate Draft PO" button the
      // normal Stage 2.1 submission unlocks (Queue.tsx renderAction case 7).
      { v: 'Stage 2.1 Completed' },
    ],
  },
  {
    group: 'Phase 2: Inspection & Finance',
    values: [
      { v: 'Under Inspection (Manual Bypass)', label: 'Force to Inspection Holding' },
      { v: 'Inspection Complete - Request FMS' },
    ],
  },
  {
    group: 'Phase 3: Depot Transit & Verification',
    values: [
      { v: 'Arrived at Depot', label: 'Arrived at Depot (Request Verify Stage)' },
      { v: 'Awaiting Depot Verification' }, { v: 'Depot Issue Reported' }, { v: 'Pipeline Complete' },
    ],
  },
];

export function OverrideStatusModal({
  indentId,
  onClose,
  currentStatus,
  isImport,
}: ActionProps & { currentStatus: string; isImport: boolean }) {
  const qc = useQueryClient();
  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');

  const groups = isImport ? IMPORT_OVERRIDE_STAGES : DOMESTIC_OVERRIDE_STAGES;

  const submit = useMutation({
    mutationFn: () => api.post(`/api/procurement/indents/${encodeURIComponent(indentId)}/force-status`, {
      newStatus,
      reason,
      password,
    }),
    onSuccess: () => {
      toast.success(`Status overridden → ${newStatus}`);
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'Override rejected'),
  });

  return (
    <ModalShell
      title="God-Mode · Force Status"
      onClose={onClose}
      onSubmit={() => submit.mutate()}
      busy={submit.isPending}
      submitLabel="Force override"
      submitDisabled={!newStatus || !reason.trim() || !password || newStatus === currentStatus}
      width="wide"
    >
      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-[11px] text-red-800 leading-relaxed">
        <b>Caution:</b> this bypasses every gate. The new status is written
        directly to the indent and a tagged entry is appended to the audit
        log. Re-enter your password to confirm.
      </div>
      <Field label="Current status">
        <div className="text-sm font-medium text-slate-700 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">{currentStatus || '—'}</div>
      </Field>
      <Field label="New status" required>
        <select className="input" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
          <option value="">— Pick a new pipeline status —</option>
          {groups.map((g) => (
            <optgroup key={g.group} label={g.group}>
              {g.values.filter((o) => o.v !== currentStatus).map((o) => (
                <option key={o.v} value={o.v}>{o.label || o.v}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </Field>
      <Field label="Reason for override" required>
        <textarea
          className="input min-h-[80px]"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why is the normal flow being skipped? This is logged."
        />
      </Field>
      <Field label="Confirm with your password" required>
        <input type="password" className="input" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
      </Field>
    </ModalShell>
  );
}

// ── Smart Gateway / FMS Payment ────────────────────────────────────────
//
// Replaces the old PaymentRequestModal. Locks to the exact 11 payment
// types the legacy Apps Script used, removes Advance_50/Balance_50, and
// after the POST opens a window into the legacy FMS app with the
// translated payment-type code so accounts can settle through the
// existing finance pipeline.
// Outbound translator — internal code → exact text the Accounts FMS dropdown
// expects. Ported verbatim from the reference `executeSmartGateway`.
const SMART_GATEWAY_TYPES: Array<{ code: string; label: string; fmsLabel: string }> = [
  { code: 'Advance_30',   label: '30% Container Advance',   fmsLabel: '30% Payment' },
  { code: 'Balance_70',   label: '70% Container Balance',   fmsLabel: 'Balance' },
  { code: 'Full_Payment', label: '100% Full Payment',       fmsLabel: 'Full Payment' },
  { code: 'Insurance',    label: 'Insurance Payment',       fmsLabel: 'Insurance' },
  { code: 'Freight',      label: 'Freight Payment',         fmsLabel: 'Freight' },
  { code: 'Local_Agent',  label: 'Local Agent Charges',     fmsLabel: 'Local Agent Charge' },
  { code: 'SIMS',         label: 'SIMS Payment',            fmsLabel: 'SIMS' },
  { code: 'Customs_Duty', label: 'Customs Duty Payment',    fmsLabel: 'Custom Duty' },
  { code: 'Stamp_Duty',   label: 'Stamp Duty Payment',      fmsLabel: 'Stamp Duty' },
  { code: 'CFS',          label: 'CFS Charges',             fmsLabel: 'CFS Charge' },
  { code: 'CHA',          label: 'CHA Charges',             fmsLabel: 'CHA Charge' },
];

// Legacy FMS (Accounts) Apps-Script web app — the reference hands off here with
// ?enquiryCaseNo=<id>&paymentType=<matched code>. Used as the default when no
// override is supplied so the hand-off works out of the box.
const DEFAULT_FMS_BASE_URL = 'https://script.google.com/a/macros/crystalgroup.in/s/AKfycbyCyEy7jwpYrIEfNkNKmtNmhkFSKqOli1EM6ZCkljgxEMGz-2l425VKJzDcBiy0q_Ghgg/exec';

// Domestic indents never touch ocean freight / customs clearance / SIMS /
// stamp duty / local-agent etc. — those categories are import-only. Mirrors
// DOMESTIC_LEDGER_KEYS in IndentDetail.tsx's Ledger tab.
const DOMESTIC_PAYMENT_CODES = new Set(['Full_Payment', 'Customs_Duty']);

export function PaymentRequestModal({
  indentId,
  onClose,
  fmsBaseUrl,
  indentType = 'Import',
}: ActionProps & { enquiryCaseNo?: string; fmsBaseUrl?: string; indentType?: string }) {
  const qc = useQueryClient();
  const isDomestic = indentType === 'Domestic';
  const gatewayTypes = isDomestic ? SMART_GATEWAY_TYPES.filter((t) => DOMESTIC_PAYMENT_CODES.has(t.code)) : SMART_GATEWAY_TYPES;
  const [paymentType, setPaymentType] = useState(isDomestic ? 'Full_Payment' : 'Advance_30');
  const [amount, setAmount] = useState('');
  // Reference parity: the legacy FMS app always handles the actual amount — the
  // request just logs the type and routes to Accounts, then hands off the
  // pre-filled FMS app. The URL falls back to the legacy app so it always works.
  const fmsUrl = fmsBaseUrl || DEFAULT_FMS_BASE_URL;
  // Pre-opened tab (see `proceed`): opening the FMS/PI form must happen inside
  // the click gesture or the popup blocker kills it — so we open a blank tab on
  // click and only set its URL once the async request resolves.
  const winRef = useRef<Window | null>(null);

  const submit = useMutation({
    // Log the request only (no indent-status change here). The indent status
    // changes ONLY when the Accounts FMS form is actually submitted — that
    // posts back to /p/procurement/fms-callback, which advances the status.
    mutationFn: () => api.post('/api/procurement/payments/request', {
      indentId, paymentType, requestedAmount: Number(amount) || 0, currency: 'INR', remarks: '',
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      // Hand off to the legacy FMS/PI app, pre-filled with the indent id +
      // translated payment code + indent type (mirrors executeSmartGateway).
      const row = SMART_GATEWAY_TYPES.find((r) => r.code === paymentType);
      try {
        const u = new URL(fmsUrl);
        u.searchParams.set('enquiryCaseNo', indentId);   // FMS keys on the indent id
        u.searchParams.set('paymentType', row?.fmsLabel || paymentType);
        u.searchParams.set('indentType', indentType);    // Import | Domestic → drives which form
        if (Number(amount) > 0) u.searchParams.set('amount', String(Number(amount)));   // pre-fill the FMS form
        if (winRef.current && !winRef.current.closed) winRef.current.location.href = u.toString();
        else window.open(u.toString(), '_blank');        // fallback if the pre-open was blocked
      } catch { winRef.current?.close(); }
      toast.success('Opening FMS — status updates once the form is submitted');
      onClose();
    },
    onError: (e: any) => { winRef.current?.close(); toast.error(e?.message || 'Request failed'); },
  });

  // Called by the Proceed button: open the tab NOW (synchronously, within the
  // gesture) then fire the request; onSuccess redirects the tab to the FMS/PI form.
  const proceed = () => {
    winRef.current = window.open('about:blank', '_blank');
    submit.mutate();
  };

  // Green "New Payment Request" modal — exact replica of the reference
  // smartGatewayModal (bg-success header, info alert, indent + category, Proceed).
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: '#1E8E3E' }}>
          <div className="flex items-center gap-2 font-bold"><Shuffle size={16} /> New Payment Request</div>
          <button className="text-white/80 hover:text-white" onClick={onClose}><X size={18} /></button>
        </div>
        {/* Body */}
        <div className="p-4 bg-slate-50 space-y-4">
          <div className="flex items-start gap-2 rounded-lg p-3 text-[11px]" style={{ background: '#d1e7dd', color: '#0f5132', border: '1px solid #badbcc' }}>
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>Select the payment type. This instantly routes the request to Accounts and opens the FMS form — the status updates when that form is submitted.</span>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Indent ID <span className="text-red-500">*</span></label>
            <input readOnly value={indentId} className="input mt-1 font-bold uppercase" style={{ borderColor: '#1E8E3E' }} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Payment Category <span className="text-red-500">*</span></label>
            <select className="input mt-1 font-bold" style={{ borderColor: '#1E8E3E' }} value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
              {gatewayTypes.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Requested Amount</label>
            <input type="number" min={0} step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="e.g. 150000"
              className="input mt-1 font-bold" style={{ borderColor: '#1E8E3E' }} />
            <p className="text-[10px] text-slate-400 mt-1">Registers in the ledger now and pre-fills the FMS form.</p>
          </div>
        </div>
        {/* Footer */}
        <div className="px-4 py-3 bg-white border-t border-slate-200 space-y-2">
          <button className="w-full py-2 rounded-lg font-bold text-slate-600 bg-slate-100 hover:bg-slate-200" onClick={onClose}>Cancel</button>
          <button
            className="w-full py-2 rounded-lg font-bold text-white inline-flex items-center justify-center gap-2 disabled:opacity-60"
            style={{ background: '#1E8E3E' }}
            disabled={submit.isPending}
            onClick={proceed}
          >
            {submit.isPending ? 'Opening…' : <>Proceed to FMS <ExternalLink size={14} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Negotiation History view ───────────────────────────────────────────
//
// Read-only timeline of every rate-change round per vendor on this
// indent. Backed by /api/procurement/awards/negotiations and grouped by
// vendor with the savings delta highlighted per round.
export function NegotiationHistoryModal({ indentId, onClose }: ActionProps) {
  const { data, isLoading } = useQuery<{ logs: any[] }>({
    queryKey: ['procurement', 'indent', indentId, 'negotiations'],
    queryFn: () => api.get(`/api/procurement/awards/negotiations/${encodeURIComponent(indentId)}`),
  });
  const rounds = data?.logs || [];

  // Group rounds by vendor for the timeline render.
  const byVendor = rounds.reduce<Record<string, any[]>>((acc, r) => {
    const k = r.vendorName || r.vendorEmail || 'Unknown';
    (acc[k] ||= []).push(r);
    return acc;
  }, {});

  return (
    <ModalShell
      title="Negotiation history"
      onClose={onClose}
      onSubmit={onClose}
      submitLabel="Close"
      width="wide"
    >
      {isLoading && <div className="text-xs text-slate-400">Loading rounds…</div>}
      {!isLoading && !rounds.length && (
        <div className="text-xs text-slate-400 italic text-center py-6">No negotiations recorded for this indent.</div>
      )}
      {Object.entries(byVendor).map(([vendor, list]) => {
        const sorted = list.slice().sort((a, b) => new Date(a.at).getTime() - new Date(b.at).getTime());
        const first = sorted[0];
        const last = sorted[sorted.length - 1];
        const totalSaved = Number(first?.oldRate || 0) - Number(last?.newRate || 0);
        const currency = last?.currency || first?.currency || '';
        return (
          <div key={vendor} className="border border-slate-200 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between border-b border-slate-100 pb-2">
              <div className="font-bold text-sm text-slate-800">{vendor}</div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">
                Saved {currency} {Number(totalSaved).toLocaleString('en-IN')}
              </div>
            </div>
            <div className="space-y-2">
              {sorted.map((r, idx) => (
                <div key={r._id || idx} className="relative pl-5 text-xs">
                  <div className="absolute left-0 top-1.5 w-2 h-2 rounded-full bg-teal-500" />
                  {idx < sorted.length - 1 && <div className="absolute left-[3px] top-3 bottom-[-8px] w-px bg-slate-200" />}
                  <div className="text-slate-700">
                    <span className="font-mono text-slate-400 line-through">{r.currency} {Number(r.oldRate || 0).toLocaleString('en-IN')}</span>
                    {' → '}
                    <span className="font-mono text-emerald-700 font-bold">{r.currency} {Number(r.newRate || 0).toLocaleString('en-IN')}</span>
                    <span className="ml-2 text-[10px] text-emerald-600">
                      (−{r.currency} {(Number(r.oldRate || 0) - Number(r.newRate || 0)).toLocaleString('en-IN')})
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Round {idx + 1} · {r.byName || 'desk'} · {r.at ? new Date(r.at).toLocaleString('en-IN') : '—'}
                  </div>
                  {r.note && <div className="text-[11px] text-slate-600 mt-0.5">{r.note}</div>}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </ModalShell>
  );
}

// ── File upload helper (shared by Contract / PI / Receipt / Vault) ─────
async function uploadFileToGridFs(file: File): Promise<{ id: string; filename: string; contentType: string }> {
  const r = await api.upload<{ id: string; filename: string; contentType: string }>('/api/files/upload', file);
  return r;
}

// ── Signed contract upload ─────────────────────────────────────────────
export function SignedContractModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFileToGridFs(file);
      await api.post('/api/procurement/awards/signed-contract', { indentId, fileId: up.id });
      toast.success('Contract uploaded');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Upload signed contract" onClose={onClose} onSubmit={submit} busy={busy} submitDisabled={!file}>
      <Field label="Signed contract (PDF / image)" required>
        <input className="input" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </Field>
    </ModalShell>
  );
}

// ── Generate PO ────────────────────────────────────────────────────────
/** Fetch a vaulted file and force-save it into the browser's Downloads folder.
 *  Uses a blob object-URL so it works even when the API is on another origin
 *  (the inline Content-Disposition / cross-origin `download` attr wouldn't). */
async function downloadVaultFile(fileId: string, filename: string): Promise<void> {
  const res = await fetch(fileUrl(fileId));
  if (!res.ok) throw new Error(`Download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

type PoLine = {
  sr?: number; containerNo: string; sizeType: string; yom: string;
  machinery: string; machinerySubCategory: string; qty: number; rate: number;
};
type PoDraftResp = {
  canGenerate: boolean;
  blockers: string[];
  alreadyGenerated: boolean;
  poNumber: string;
  indentType: string;
  vendor: { name: string; address: string; city: string; country: string; gst: string; cin: string; email: string };
  draft: {
    currency: string; gstRate: number; dispatchLocation: string;
    paymentTerms: string; contactPerson: string; requirement: string; lines: PoLine[];
  };
};

/**
 * Generate PO — auto-fetched, then editable.
 *
 * Everything on screen comes from `GET /pos/:id/draft`, which is the SAME
 * resolver the PDF renders from (awarded quote tiers -> containers -> header
 * qty). So what the purchaser sees is what prints, and any correction they make
 * is handed straight back, rather than the form second-guessing those joins and
 * quietly drifting from the document.
 *
 * The flow gates come from the server too (`blockers`), so this can't be used to
 * cut a PO the chain isn't ready for: no vendor awarded, nothing to bill, or
 * every line still at a zero rate.
 */
export function GeneratePoModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const [poNumber, setPoNumber] = useState('');
  const [lines, setLines] = useState<PoLine[] | null>(null);
  const [terms, setTerms] = useState({ gstRate: 0, dispatchLocation: '', paymentTerms: '', contactPerson: '', requirement: '' });
  const [seeded, setSeeded] = useState(false);

  const { data, isLoading } = useQuery<PoDraftResp>({
    queryKey: ['procurement', 'po-draft', indentId],
    queryFn: () => api.get(`/api/procurement/pos/${encodeURIComponent(indentId)}/draft`),
  });

  // Seed the editable copy once, when the draft lands.
  if (data && !seeded) {
    setLines(data.draft.lines.map((l) => ({ ...l })));
    setTerms({
      gstRate: data.draft.gstRate,
      dispatchLocation: data.draft.dispatchLocation,
      paymentTerms: data.draft.paymentTerms,
      contactPerson: data.draft.contactPerson,
      requirement: data.draft.requirement,
    });
    setSeeded(true);
  }

  const ccy = data?.draft.currency || 'INR';
  const rows = lines || [];
  const base = rows.reduce((sum, l) => sum + (Number(l.qty) || 0) * (Number(l.rate) || 0), 0);
  const gstValue = (base * (Number(terms.gstRate) || 0)) / 100;
  const grand = base + gstValue;
  const money = (n: number) => `${ccy} ${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const setLine = (i: number, patch: Partial<PoLine>) =>
    setLines((prev) => (prev || []).map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () =>
    setLines((prev) => [...(prev || []), { containerNo: '', sizeType: '', yom: '', machinery: '', machinerySubCategory: '', qty: 1, rate: 0 }]);
  const removeLine = (i: number) => setLines((prev) => (prev || []).filter((_, idx) => idx !== i));

  const submit = useMutation({
    mutationFn: () => api.post<{ poNumber: string; poFileId: string }>(
      `/api/procurement/pos/${encodeURIComponent(indentId)}/generate`,
      { lines: rows, ...terms },
    ),
    onSuccess: async (r) => {
      setPoNumber(r.poNumber);
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      try {
        await downloadVaultFile(r.poFileId, `${r.poNumber.split('/').join('_')}.pdf`);
        toast.success(`PO ${r.poNumber} generated & downloaded`);
      } catch {
        toast.success(`PO ${r.poNumber} generated`);
        toast.error('Auto-download failed — open it from the Master Vault.');
      }
    },
    onError: (e: any) => toast.error(e?.message || 'PO generation failed'),
  });

  const blocked = !!data && !data.canGenerate;
  const cell = 'w-full rounded border border-slate-200 px-1.5 py-1 text-[11px]';

  return (
    <ModalShell
      title="Generate Purchase Order"
      onClose={onClose}
      onSubmit={() => (poNumber ? onClose() : submit.mutate())}
      busy={submit.isPending}
      submitLabel={poNumber ? 'Close' : 'Generate PO'}
      submitDisabled={!poNumber && (blocked || isLoading || rows.length === 0)}
      width="wide"
    >
      {poNumber ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm">
          <div className="font-bold text-emerald-800">PO {poNumber}</div>
          <div className="mt-1 text-[11px] text-emerald-700">Downloaded to your Downloads folder · attached to the Master Vault.</div>
          <div className="mt-1 text-[11px] text-emerald-700">CEO approval skipped (handled externally). <b>Next: Request FMS.</b></div>
        </div>
      ) : isLoading ? (
        <div className="py-6 text-center text-xs text-slate-500">Fetching PO details…</div>
      ) : (
        <div className="space-y-3">
          {blocked && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-[11.5px] text-rose-800">
              <div className="font-semibold">Not ready for a PO yet</div>
              <ul className="mt-1 list-disc pl-4">{data!.blockers.map((b) => <li key={b}>{b}</li>)}</ul>
            </div>
          )}
          {data?.alreadyGenerated && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11.5px] text-amber-900">
              PO <b>{data.poNumber}</b> already exists for this indent. Generating again mints a NEW number and replaces the vaulted copy.
            </div>
          )}

          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11.5px]">
            <span className="font-semibold text-slate-700">Vendor:</span>{' '}
            {data?.vendor.name || '—'}
            {data?.vendor.gst ? <span className="text-slate-500"> · GSTIN {data.vendor.gst}</span> : null}
            {data?.vendor.email ? <span className="text-slate-500"> · {data.vendor.email}</span> : null}
            <div className="mt-0.5 text-[10.5px] text-slate-500">Auto-fetched from the awarded quote. Correct anything below before generating.</div>
          </div>

          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full min-w-[46rem] text-left text-[11px]">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-1.5 py-1.5">Description</th>
                  <th className="px-1.5 py-1.5">Machinery</th>
                  <th className="px-1.5 py-1.5">Sub-category</th>
                  <th className="px-1.5 py-1.5">YOM</th>
                  <th className="px-1.5 py-1.5 text-right">Qty</th>
                  <th className="px-1.5 py-1.5 text-right">Rate</th>
                  <th className="px-1.5 py-1.5 text-right">Amount</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-1.5 py-1"><input className={cell} value={l.sizeType} onChange={(e) => setLine(i, { sizeType: e.target.value })} /></td>
                    <td className="px-1.5 py-1"><input className={cell} value={l.machinery} onChange={(e) => setLine(i, { machinery: e.target.value })} /></td>
                    <td className="px-1.5 py-1"><input className={cell} value={l.machinerySubCategory} onChange={(e) => setLine(i, { machinerySubCategory: e.target.value })} /></td>
                    <td className="px-1.5 py-1"><input className={`${cell} w-16`} value={l.yom} onChange={(e) => setLine(i, { yom: e.target.value })} /></td>
                    <td className="px-1.5 py-1"><input type="number" min={0} className={`${cell} text-right`} value={l.qty} onChange={(e) => setLine(i, { qty: Number(e.target.value) || 0 })} /></td>
                    <td className="px-1.5 py-1"><input type="number" min={0} className={`${cell} text-right`} value={l.rate} onChange={(e) => setLine(i, { rate: Number(e.target.value) || 0 })} /></td>
                    <td className="px-1.5 py-1 text-right font-semibold tabular-nums">{money((Number(l.qty) || 0) * (Number(l.rate) || 0))}</td>
                    <td className="px-1.5 py-1 text-center">
                      <button type="button" className="text-slate-400 hover:text-rose-600" onClick={() => removeLine(i)} title="Remove line">×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button type="button" className="btn-ghost text-xs" onClick={addLine}>+ Add line</button>

          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <Field label="Payment terms"><input className="input" value={terms.paymentTerms} onChange={(e) => setTerms({ ...terms, paymentTerms: e.target.value })} /></Field>
            <Field label="Delivery / dispatch location"><input className="input" value={terms.dispatchLocation} onChange={(e) => setTerms({ ...terms, dispatchLocation: e.target.value })} /></Field>
            <Field label="Contact person"><input className="input" value={terms.contactPerson} onChange={(e) => setTerms({ ...terms, contactPerson: e.target.value })} /></Field>
            <Field label="Requirement"><input className="input" value={terms.requirement} onChange={(e) => setTerms({ ...terms, requirement: e.target.value })} /></Field>
            <Field label="GST %"><input type="number" min={0} max={100} className="input" value={terms.gstRate} onChange={(e) => setTerms({ ...terms, gstRate: Number(e.target.value) || 0 })} /></Field>
          </div>

          <div className="ml-auto w-full max-w-xs space-y-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px]">
            <div className="flex justify-between"><span className="text-slate-500">Base amount</span><span className="tabular-nums">{money(base)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">GST @ {terms.gstRate}%</span><span className="tabular-nums">{money(gstValue)}</span></div>
            <div className="flex justify-between border-t border-slate-300 pt-1 font-bold"><span>Grand total</span><span className="tabular-nums">{money(grand)}</span></div>
          </div>
        </div>
      )}
    </ModalShell>
  );
}

// ── Upload Proforma Invoice ────────────────────────────────────────────
export function ProformaModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFileToGridFs(file);
      await api.post(`/api/procurement/pos/${encodeURIComponent(indentId)}/proforma-invoice`, { fileId: up.id });
      toast.success('PI uploaded');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Upload Proforma Invoice" onClose={onClose} onSubmit={submit} busy={busy} submitDisabled={!file}>
      <Field label="Vendor's Proforma Invoice (PDF / image)" required>
        <input className="input" type="file" accept="application/pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} />
      </Field>
    </ModalShell>
  );
}

// ── TAT start ──────────────────────────────────────────────────────────
export function StartTatModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const [targetDate, setTargetDate] = useState('');

  const submit = useMutation({
    mutationFn: () => api.post(`/api/procurement/manufacturing/${encodeURIComponent(indentId)}/start-tat`, { targetDate }),
    onSuccess: () => {
      toast.success('Manufacturing TAT started');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to start TAT'),
  });

  return (
    <ModalShell title="Start manufacturing TAT" onClose={onClose} onSubmit={() => submit.mutate()} busy={submit.isPending} submitDisabled={!targetDate}>
      <Field label="Target completion date" required>
        <input type="date" className="input" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
      </Field>
      <div className="text-[11px] text-slate-400">
        The cron will fire reminder emails to the vendor at 50%, 75%, and 100% of the elapsed window.
      </div>
    </ModalShell>
  );
}

// ── Fulfillment / Completion docs (marks Manufacturing Complete) ───────
// Exact replica of the reference "Upload Fulfillment Docs" modal: CSC +
// Commissionary + Container Photos (.zip) required, Test Certificate optional.
// On submit it vaults all four and flips the indent to "Manufacturing Complete"
// (which auto-stages the 70% balance), mirroring `uploadContainerDocs`.
export function FulfillmentDocsModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const [csc, setCsc] = useState<File | null>(null);
  const [comm, setComm] = useState<File | null>(null);
  const [photos, setPhotos] = useState<File | null>(null);
  const [test, setTest] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!csc || !comm || !photos) { toast.error('CSC, Commissionary & Container Photos are required.'); return; }
    setBusy(true);
    try {
      const [cscU, commU, photosU] = await Promise.all([
        uploadFileToGridFs(csc), uploadFileToGridFs(comm), uploadFileToGridFs(photos),
      ]);
      const testU = test ? await uploadFileToGridFs(test) : null;
      await api.post(`/api/procurement/manufacturing/${encodeURIComponent(indentId)}/completion-docs`, {
        cscFileId: cscU.id, commFileId: commU.id, photosZipFileId: photosU.id,
        ...(testU ? { testCertFileId: testU.id } : {}),
      });
      toast.success('Docs vaulted · manufacturing marked complete');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      qc.invalidateQueries({ queryKey: ['procurement', 'import-queue'] });
      onClose();
    } catch (e: any) { toast.error(e?.message || 'Upload failed'); } finally { setBusy(false); }
  };

  const fileRow = (n: number, label: string, accept: string, set: (f: File | null) => void, required = true) => (
    <div>
      <div className="text-sm font-bold text-slate-100 mb-1">{n}. {label} {required ? <span className="text-red-400">*</span> : <span className="text-slate-400 font-normal">(Optional)</span>}</div>
      <input type="file" accept={accept} onChange={(e) => set(e.target.files?.[0] || null)}
        className="w-full text-xs text-slate-300 bg-slate-800 border border-slate-700 rounded-lg p-2 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-slate-700 file:text-slate-200 file:font-medium" />
    </div>
  );

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-xl shadow-2xl overflow-hidden" style={{ background: '#1e293b' }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: '#0B1330' }}>
          <div className="flex items-center gap-2 font-bold"><Package size={16} /> Upload Fulfillment Docs</div>
          <button className="text-slate-400 hover:text-white" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="flex items-start gap-2 rounded-lg p-2.5 text-[11px] text-slate-300" style={{ background: '#0B1330', border: '1px solid #374151' }}>
            <ShieldCheck size={14} className="shrink-0 mt-0.5 text-slate-400" />
            <span>Uploading these documents will mark the manufacturing as complete and disable all automated vendor reminders.</span>
          </div>
          {fileRow(1, 'CSC Certificate (.pdf)', '.pdf', setCsc)}
          {fileRow(2, 'Commissionary Certificate (.pdf)', '.pdf', setComm)}
          {fileRow(3, 'Container Photos (.zip)', '.zip,.rar', setPhotos)}
          {fileRow(4, 'Test Certificate (.pdf)', '.pdf', setTest, false)}
        </div>
        <div className="px-4 py-3 flex justify-end gap-2" style={{ background: '#0B1330' }}>
          <button className="px-4 py-2 rounded-lg text-sm font-bold text-slate-300 bg-slate-700 hover:bg-slate-600" onClick={onClose}>Cancel</button>
          <button className="px-4 py-2 rounded-lg text-sm font-bold text-white inline-flex items-center gap-2 disabled:opacity-60"
            style={{ background: '#0b1220', border: '1px solid #374151' }} disabled={busy} onClick={submit}>
            {busy ? 'Securing…' : <>Secure Vault &amp; Complete <Lock size={13} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Vessel assignment ──────────────────────────────────────────────────
// Exact replica of the reference `vesselModal` ("Update Vessel & Tracking
// Details") — cyan header + info banner, Master Tracking prefilled with the
// attached container numbers, REQUIRED local agent + BL number + BL upload,
// "Confirm 'On Water'" CTA. Submit → /vessel → "Vessel Assigned - Awaiting Departure".
export function VesselAssignModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const { data: ind } = useQuery<{ indent: any }>({ queryKey: ['procurement', 'indent', indentId], queryFn: () => api.get(`/api/procurement/indents/${encodeURIComponent(indentId)}`) });
  const { data: agentsData } = useQuery<{ rows: Array<{ name: string; email?: string }> }>({ queryKey: ['procurement', 'masters', 'local-agents-options'], queryFn: () => api.get('/api/procurement/masters/local-agents-options') });
  const agents = (agentsData?.rows || []).filter((a) => a?.name);
  // Ordered vs already shipped vs still to book, straight from the server.
  const { data: fulfilment } = useFulfilment(indentId);
  const [form, setForm] = useState({
    shippingLine: '', vesselName: '', voyageNo: '', trackingNumber: '', blNumber: '',
    etdDeparture: '', etaArrival: '', assignedLocalAgent: '',
    // Resolved from the vessel name by VesselResolver. MMSI is what AISStream
    // subscribes to — without it the order never appears on the tracking board.
    imoNumber: '', mmsi: '',
  });
  /**
   * The containers going on THIS vessel.
   *
   * A vendor with 5 of 10 ready ships those 5 now; the rest wait for the next
   * sailing with its own vessel, voyage and ETA. When no serials are on file
   * yet the tracking box takes over and everything typed there travels
   * together — the old behaviour, unchanged.
   */
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [blFile, setBlFile] = useState<File | null>(null);
  const [pklFile, setPklFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (v: string) => setForm((p) => ({ ...p, [k]: v }));

  /** Serials we hold that are not yet on a sailing — the pickable list. */
  const available = fulfilment?.unassignedContainerNos ?? [];
  const availableKey = available.join(',');

  // Tick everything unshipped by default: sending the whole remainder is the
  // common case, and un-ticking a few is quicker than picking them one by one.
  useEffect(() => {
    if (!available.length) return;
    setPicked((prev) => (prev.size ? prev : new Set(available)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableKey]);

  // The tracking field mirrors the ticks so the two can never disagree about
  // what is being shipped. It stays editable when no serials exist yet.
  useEffect(() => {
    if (!available.length) {
      const cn = ind?.indent?.containerNumbers;
      if (cn) setForm((p) => (p.trackingNumber ? p : { ...p, trackingNumber: String(cn) }));
      return;
    }
    setForm((p) => ({ ...p, trackingNumber: Array.from(picked).join('\n') }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked, availableKey, ind?.indent?.containerNumbers]);

  const toggle = (n: string) => setPicked((prev) => {
    const next = new Set(prev);
    if (next.has(n)) next.delete(n); else next.add(n);
    return next;
  });

  const selecting = available.length
    ? picked.size
    : form.trackingNumber.split(/[,\n]/).map((s) => s.trim()).filter(Boolean).length;
  const nothingLeft = !!fulfilment && fulfilment.pendingShipment === 0 && available.length === 0;

  const ready = !!(form.shippingLine && form.vesselName && form.assignedLocalAgent && form.blNumber && blFile
    && selecting > 0);
  const lbl = 'text-[10px] uppercase tracking-wider font-bold text-slate-500';

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      const payload: any = { ...form };
      // Only send the explicit list when there are serials to pick from;
      // otherwise the backend parses the tracking field, which is what older
      // clients and the magic-link flows send.
      if (available.length) payload.containerNos = Array.from(picked);
      if (blFile) payload.blFileId = (await uploadFileToGridFs(blFile)).id;
      if (pklFile) payload.packingListFileId = (await uploadFileToGridFs(pklFile)).id;
      const res = await api.post<{
        batch?: { batchNo: number; quantity: number };
        fulfilment?: { shipped: number; ordered: number; pendingShipment: number };
      }>(`/api/procurement/logistics/${encodeURIComponent(indentId)}/vessel`, payload);
      const f = res?.fulfilment;
      toast.success(
        f
          ? `Batch ${res?.batch?.batchNo ?? ''} away · ${f.shipped}/${f.ordered} shipped`
            + (f.pendingShipment > 0 ? ` · ${f.pendingShipment} still to book` : ' · order complete')
          : 'Vessel updated · pushed to live tracking board',
      );
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      qc.invalidateQueries({ queryKey: ['procurement', 'fulfilment', indentId] });
      qc.invalidateQueries({ queryKey: ['procurement', 'import-queue'] });
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Vessel save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden bg-white max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 text-white shrink-0" style={{ background: '#1A73E8' }}>
          <div className="flex items-center gap-2 font-bold">
            <Ship size={16} /> Update Vessel &amp; Tracking Details
            {!!fulfilment?.batches.length && (
              <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold">
                Batch {fulfilment.batches.length + 1}
              </span>
            )}
          </div>
          <button className="text-white/80 hover:text-white" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3 overflow-y-auto">
          <div className="flex items-start gap-2 rounded-lg p-3 text-[11px]" style={{ background: '#cff4fc', color: '#055160', border: '1px solid #b6effb' }}>
            <Radio size={14} className="shrink-0 mt-0.5" />
            <span>
              Shipping details for <b>this sailing</b>. Containers you do not tick stay pending and can go on a later
              vessel with their own voyage and ETA. This pushes the batch to the live tracking board.
            </span>
          </div>

          {/* Ordered vs shipped vs what this sailing takes. */}
          <FulfilmentSummary f={fulfilment} stage="shipping" selecting={selecting} />

          {nothingLeft && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] font-semibold text-emerald-800">
              Every ordered container is already on a sailing — nothing left to assign.
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><label className={lbl}>Shipping Line</label><input className="input mt-1" value={form.shippingLine} onChange={(e) => set('shippingLine')(e.target.value)} placeholder="e.g. Maersk, MSC" /></div>
            <div>
              <label className={lbl}>Vessel Name</label>
              <input className="input mt-1" value={form.vesselName} onChange={(e) => set('vesselName')(e.target.value)} placeholder="e.g. MSC Isabella" />
            </div>
            <div>
              <label className={lbl}>Voyage No.</label>
              <input className="input mt-1 font-mono" value={form.voyageNo} onChange={(e) => set('voyageNo')(e.target.value)} placeholder="e.g. V.123E" />
            </div>
          </div>
          {/* Turns the typed name into the IMO/MMSI that live tracking needs. */}
          <VesselResolver
            vesselName={form.vesselName}
            imo={form.imoNumber}
            mmsi={form.mmsi}
            onPick={({ imo, mmsi, name }) =>
              setForm((p) => ({ ...p, imoNumber: imo, mmsi, vesselName: name || p.vesselName }))}
          />

          {available.length > 0 ? (
            <div>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <label className={lbl}>Containers on this sailing <span className="text-red-500">*</span></label>
                <div className="flex gap-2">
                  <button type="button" className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => setPicked(new Set(available))}>All {available.length}</button>
                  <button type="button" className="rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                    onClick={() => setPicked(new Set())}>None</button>
                </div>
              </div>
              <div className="mt-1 grid max-h-44 grid-cols-2 gap-1 overflow-y-auto rounded-lg border border-slate-200 p-2 sm:grid-cols-3">
                {available.map((n) => (
                  <label key={n} className={`flex cursor-pointer items-center gap-2 rounded px-1.5 py-1 text-[12px] ${picked.has(n) ? 'bg-sky-50' : 'hover:bg-slate-50'}`}>
                    <input type="checkbox" checked={picked.has(n)} onChange={() => toggle(n)} />
                    <span className="truncate font-mono">{n}</span>
                  </label>
                ))}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {picked.size} of {available.length} unshipped container{available.length === 1 ? '' : 's'} selected.
                {available.length - picked.size > 0 && ` ${available.length - picked.size} stay pending for a later vessel.`}
              </div>
            </div>
          ) : (
            <div>
              <label className={lbl}>Master Tracking / Container Number(s)</label>
              <textarea className="input mt-1 min-h-[70px] font-mono text-sky-600 font-bold" value={form.trackingNumber} onChange={(e) => set('trackingNumber')(e.target.value)} placeholder={'MSCU1234567\nHLXU7654321'} />
              <div className="mt-1 text-[11px] text-slate-500">
                No container serials on file yet — type the ones sailing now, one per line.
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className={lbl}>ETD (Departure)</label><input type="date" className="input mt-1" value={form.etdDeparture} onChange={(e) => set('etdDeparture')(e.target.value)} /></div>
            <div><label className={lbl}>ETA (Arrival)</label><input type="date" className="input mt-1" value={form.etaArrival} onChange={(e) => set('etaArrival')(e.target.value)} /></div>
          </div>
          <div>
            <label className={lbl}>Assign Local Agent (Depot Pickup) <span className="text-red-500">*</span></label>
            <select className="input mt-1 font-bold text-sky-600" value={form.assignedLocalAgent} onChange={(e) => set('assignedLocalAgent')(e.target.value)}>
              <option value="">-- Select Local Agent --</option>
              {agents.map((a) => <option key={a.name} value={a.name}>{a.name}{a.email ? ` · ${a.email}` : ''}</option>)}
            </select>
          </div>
          <div className="border-t border-slate-200 pt-3">
            <label className={lbl}>Bill of Lading (BL) Document Number <span className="text-red-500">*</span></label>
            <input className="input mt-1 font-mono text-sky-600 font-bold" value={form.blNumber} onChange={(e) => set('blNumber')(e.target.value)} placeholder="e.g. SHSL260326134A" />
            <div className="mt-1 text-[11px] text-slate-500">One BL per sailing — a later batch gets its own.</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><label className={lbl}>Upload Bill of Lading (BL) <span className="text-red-500">*</span></label><input type="file" accept="application/pdf" className="input mt-1" style={{ borderColor: '#1A73E8' }} onChange={(e) => setBlFile(e.target.files?.[0] || null)} /></div>
            <div><label className={lbl}>Upload Packing List (Optional)</label><input type="file" accept="application/pdf" className="input mt-1" style={{ borderColor: '#1A73E8' }} onChange={(e) => setPklFile(e.target.files?.[0] || null)} /></div>
          </div>

          {/* Sailings already booked for this indent. */}
          {!!fulfilment?.batches.length && (
            <div>
              <div className={`${lbl} mb-1`}>Already sailing</div>
              <BatchList f={fulfilment} />
            </div>
          )}
        </div>
        <div className="px-4 py-3 bg-white border-t border-slate-200 flex justify-end gap-2 shrink-0">
          <button className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200" onClick={onClose}>Cancel</button>
          <button className="px-4 py-2 rounded-lg text-sm font-bold text-slate-900 inline-flex items-center gap-2 disabled:opacity-60"
            style={{ background: '#1A73E8' }} disabled={busy || !ready} onClick={submit}>
            {busy ? 'Saving…' : <><Radio size={13} /> Confirm On Water ({selecting})</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CHA Dispatch (strict 6-doc bundle) ─────────────────────────────────
//
// Port of executeChaDispatchBackend. Operator picks a CHA agent +
// uploads Insurance Doc + COO Doc; backend verifies the 4 pre-existing
// vaulted docs (Final Invoice, Packing List, BL, SIMS) are present and
// dispatches the bundle, transitioning the indent to "Customs Cleared".
export function ChaDispatchModal({ indentId, onClose, chaAgents }: ActionProps & { chaAgents?: Array<{ name: string; email: string }> }) {
  const qc = useQueryClient();
  // Self-fetch the CHA agent directory (reference parity: getActiveLocalAgents)
  // so the dropdown always populates — the parent render sites don't pass a list,
  // which is why "CHA Agent" showed only "— Select —" and dispatch was blocked.
  const { data: chaData } = useQuery<{ rows: Array<{ name: string; email: string }> }>({
    queryKey: ['procurement', 'masters', 'cha-agents'],
    queryFn: () => api.get('/api/procurement/masters/cha-agents'),
  });
  const agents = ((chaData?.rows?.length ? chaData.rows : chaAgents) || []).filter((a) => a?.email);
  const [chaEmail, setChaEmail] = useState('');
  const [insurance, setInsurance] = useState<File | null>(null);
  const [coo, setCoo] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!insurance || !coo || !chaEmail) return;
    setBusy(true);
    try {
      const [ins, c] = await Promise.all([uploadFileToGridFs(insurance), uploadFileToGridFs(coo)]);
      await api.post(`/api/procurement/logistics/${encodeURIComponent(indentId)}/cha-dispatch`, {
        chaEmail,
        insuranceDocFileId: ins.id,
        cooDocFileId: c.id,
      });
      toast.success('CHA dispatched');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'CHA dispatch failed (check that all 4 vaulted docs are uploaded first)');
    } finally {
      setBusy(false);
    }
  };

  // Exact replica of the reference chaDispatchModal — dark header, the 4
  // securely-vaulted docs shown with green checks, upload the final 2, pick CHA.
  const vaulted = ['Commercial Invoice', 'Packing List', 'Bill of Lading (BL)', 'SIMS Certificate (Accounts)'];
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 text-white" style={{ background: '#010101' }}>
          <div className="flex items-center gap-2 font-bold"><Send size={16} /> Dispatch Customs Bundle (CHA)</div>
          <button className="text-slate-300 hover:text-white" onClick={onClose}><X size={18} /></button>
        </div>
        {/* Body */}
        <div className="p-4 bg-slate-50 space-y-4">
          <div className="flex items-start gap-2 rounded-lg p-3 text-[11px]" style={{ background: '#e9ecef', color: '#41464b', border: '1px solid #d3d6d8' }}>
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>The system has already secured 4 documents. Upload the final 2 missing documents to dispatch the complete 6-document bundle to the CHA.</span>
          </div>

          {/* Securely vaulted docs */}
          <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
            <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase text-emerald-600 border-b border-slate-100 pb-2 mb-2"><Lock size={12} /> Securely Vaulted Documents</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs font-semibold text-slate-500">
              {vaulted.map((d) => (
                <div key={d} className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500" /> {d}</div>
              ))}
            </div>
          </div>

          {/* Upload missing */}
          <div>
            <div className="text-[11px] font-bold uppercase text-blue-600 border-b border-slate-200 pb-2 mb-2">Upload Missing Documents</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Insurance Policy (.pdf) <span className="text-red-500">*</span></label>
                <input type="file" accept="application/pdf" className="input mt-1" style={{ borderColor: '#052379' }} onChange={(e) => setInsurance(e.target.files?.[0] || null)} />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Certificate of Origin (COO) (.pdf) <span className="text-red-500">*</span></label>
                <input type="file" accept="application/pdf" className="input mt-1" style={{ borderColor: '#052379' }} onChange={(e) => setCoo(e.target.files?.[0] || null)} />
              </div>
            </div>
          </div>

          {/* Select CHA */}
          <div>
            <div className="text-[11px] font-bold uppercase text-slate-700 border-b border-slate-200 pb-2 mb-2">Select Customs Agent</div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Target CHA Partner <span className="text-red-500">*</span></label>
            <select className="input mt-1 font-bold" value={chaEmail} onChange={(e) => setChaEmail(e.target.value)}>
              <option value="">-- Select CHA Partner --</option>
              {agents.map((a) => <option key={a.email} value={a.email}>{a.name} · {a.email}</option>)}
            </select>
          </div>
        </div>
        {/* Footer */}
        <div className="px-4 py-3 bg-white border-t border-slate-200 flex justify-end gap-2">
          <button className="px-4 py-2 rounded-lg text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200" onClick={onClose}>Cancel</button>
          <button className="px-4 py-2 rounded-lg text-sm font-bold text-white inline-flex items-center gap-2 disabled:opacity-60"
            style={{ background: '#010101' }} disabled={busy || !insurance || !coo || !chaEmail} onClick={submit}>
            {busy ? 'Dispatching…' : <>Bundle &amp; Dispatch <Send size={13} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SIMS Registration (Complete SIMS) ─────────────────────────────────
// Exact replica of the reference `openSimsModal` → processSimsRegistration.
// Shipment + Customs/CIF details; CIF (INR) auto-calcs from FC × rate.
export function SimsModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    blNo: '', invoiceNo: '', blDate: '', coo: '', itemSl: '',
    portExport: '', portImport: '', dateExport: '', dateImport: '',
    qty: '', currency: 'US Dollar', cifFc: '', conversionRate: '',
  });
  const set = (k: string) => (v: string) => setF((p) => ({ ...p, [k]: v }));

  // Auto-fetch BL Number (set at Update Vessel), Invoice Number (set at
  // Dispatch), and the FX conversion rate (locked at award) from the indent.
  // Only fills blank fields so operator edits are never clobbered.
  const { data: ind } = useQuery<{ indent: any }>({ queryKey: ['procurement', 'indent', indentId], queryFn: () => api.get(`/api/procurement/indents/${encodeURIComponent(indentId)}`) });
  const indent = ind?.indent;
  useEffect(() => {
    if (!indent) return;
    const cur = String(indent.awardedCurrency || indent.expectedCurrency || '').toUpperCase();
    const curLabel = cur.includes('EUR') ? 'Euro' : (cur.includes('AED') || cur.includes('DIRHAM') ? 'Dirham' : 'US Dollar');
    const rate = Number(indent.lockedExchangeRate) || Number(indent.expectedExchangeRate) || 0;
    setF((p) => ({
      ...p,
      blNo: p.blNo || indent.blNumber || '',
      invoiceNo: p.invoiceNo || indent.invoiceNo || '',
      conversionRate: p.conversionRate || (rate > 0 ? String(rate) : ''),
      currency: p.currency === 'US Dollar' ? curLabel : p.currency,
    }));
  }, [indent]);

  const cifInr = (Number(f.cifFc) || 0) * (Number(f.conversionRate) || 0);
  const submit = useMutation({
    mutationFn: () => api.post('/api/procurement/payments/sims-register', {
      indentId, ...f, qty: Number(f.qty) || 0, cifFc: Number(f.cifFc) || 0,
      conversionRate: Number(f.conversionRate) || 0, cifInr,
    }),
    onSuccess: () => {
      toast.success('SIMS registered · sent to Accounts');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      qc.invalidateQueries({ queryKey: ['procurement', 'import-queue'] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'SIMS registration failed'),
  });
  const ready = f.blNo && f.invoiceNo && f.blDate && f.coo && f.itemSl && f.portExport && f.portImport && f.dateExport && f.dateImport && Number(f.qty) > 0 && Number(f.cifFc) > 0 && Number(f.conversionRate) > 0;

  return (
    <ModalShell title="Complete SIMS Registration" onClose={onClose} onSubmit={() => submit.mutate()} busy={submit.isPending} submitLabel="Register SIMS" submitDisabled={!ready} width="wide">
      <div className="text-[10px] uppercase tracking-wider text-teal-700 font-bold">Shipment Details</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="BL Number" required><input className="input" value={f.blNo} onChange={(e) => set('blNo')(e.target.value)} placeholder="Type BL Number here" /></Field>
        <Field label="Invoice Number" required><input className="input" value={f.invoiceNo} onChange={(e) => set('invoiceNo')(e.target.value)} placeholder="Type Invoice Number here" /></Field>
        <Field label="BL Date" required><input type="date" className="input" value={f.blDate} onChange={(e) => set('blDate')(e.target.value)} /></Field>
        <Field label="Country of Origin" required><input className="input" value={f.coo} onChange={(e) => set('coo')(e.target.value)} placeholder="e.g. China" /></Field>
        <Field label="Item Sl. No. (HS Code)" required><input className="input" value={f.itemSl} onChange={(e) => set('itemSl')(e.target.value)} /></Field>
        <div />
        <Field label="Port of Exportation" required><input className="input" value={f.portExport} onChange={(e) => set('portExport')(e.target.value)} placeholder="e.g. SHANGHAI" /></Field>
        <Field label="Port of Import" required><input className="input" value={f.portImport} onChange={(e) => set('portImport')(e.target.value)} placeholder="e.g. NHAVA SHEVA" /></Field>
        <Field label="Expected Date of Export" required><input type="date" className="input" value={f.dateExport} onChange={(e) => set('dateExport')(e.target.value)} /></Field>
        <Field label="Expected Date of Import" required><input type="date" className="input" value={f.dateImport} onChange={(e) => set('dateImport')(e.target.value)} /></Field>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold mt-2">Customs &amp; CIF Details</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Quantity (Metric Tonnes)" required><input type="number" step="0.01" min={0} className="input" value={f.qty} onChange={(e) => set('qty')(e.target.value)} placeholder="e.g. 7.50" /></Field>
        <Field label="Foreign Currency (FFC)" required>
          <select className="input" value={f.currency} onChange={(e) => set('currency')(e.target.value)}>
            <option>US Dollar</option><option>Euro</option><option>Dirham</option>
          </select>
        </Field>
        <Field label="CIF Value (in FFC)" required><input type="number" step="0.01" min={0} className="input" value={f.cifFc} onChange={(e) => set('cifFc')(e.target.value)} placeholder="e.g. 5800" /></Field>
        <Field label="FFC Conversion Rate" required><input type="number" step="0.01" min={0} className="input" value={f.conversionRate} onChange={(e) => set('conversionRate')(e.target.value)} placeholder="e.g. 92.5" /></Field>
        <Field label="CIF Value (INR) · auto-calc"><div className="input bg-slate-50 font-bold text-slate-700">₹ {cifInr.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></Field>
      </div>
    </ModalShell>
  );
}

// ── Depot Verification Status (View Depot Status) ─────────────────────
// Replica of the reference depotStatusModal — per-container gate-in status +
// a deep-link to the live Gate-In portal.
const GATE_IN_PORTAL = 'https://script.google.com/a/macros/crystalgroup.in/s/AKfycbxJSCf8M4rDCGHrtuaY1p0okyLa3sM73RvUeS9UC4TImdxDbOTqLZm9WJRp7B4nWtwm9w/exec';
export function DepotStatusModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<{ data: any[] }>({
    queryKey: ['procurement', 'gate-in', indentId],
    queryFn: () => api.get(`/api/procurement/containers/gate-in/${encodeURIComponent(indentId)}`),
  });
  const rows = data?.data || [];
  const isIn = (s: string) => /in stock/i.test(String(s || ''));
  const total = rows.filter((r: any) => !/\[WIP\]|^UNASSIGNED/i.test(String(r.cNo || ''))).length;
  const verified = rows.filter((r: any) => isIn(r.status)).length;
  const [busyCno, setBusyCno] = useState('');

  const gateIn = async (cNo: string) => {
    setBusyCno(cNo);
    try {
      const r = await api.post<{ pipelineComplete: boolean; verified: number; total: number }>(`/api/procurement/containers/gate-in/${encodeURIComponent(indentId)}`, { containerNo: cNo });
      qc.invalidateQueries({ queryKey: ['procurement', 'gate-in', indentId] });
      qc.invalidateQueries({ queryKey: ['procurement', 'import-queue'] });
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      toast.success(r.pipelineComplete ? `All ${r.total} containers gated in — Pipeline Complete` : `Gated in (${r.verified}/${r.total})`);
    } catch (e: any) {
      toast.error(e?.message || 'Gate-in failed');
    } finally { setBusyCno(''); }
  };

  const badge = (s: string) => {
    const v = String(s || '').toLowerCase();
    if (/verified|in stock/.test(v)) return 'pc-bg-success';
    if (/pending|await|gate|transit|cfs|customs|arrived/.test(v)) return 'pc-bg-warning';
    return 'pc-bg-secondary';
  };
  return (
    <ModalShell title="Depot Verification / Gate-In" onClose={onClose}
      onSubmit={() => window.open(`${GATE_IN_PORTAL}?indent=${encodeURIComponent(indentId)}`, '_blank')}
      submitLabel="Open Live Gate-In Portal" width="wide">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-500">
        <span>Gate in each container as it arrives. When all are in, the indent auto-completes.</span>
        <span className="font-bold text-slate-700">{verified}/{total} gated in</span>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b"><tr><th className="text-left px-3 py-2 font-semibold">Container No</th><th className="text-center px-3 py-2 font-semibold">Status</th><th className="text-right px-3 py-2 font-semibold">Action</th></tr></thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading && <tr><td colSpan={3} className="text-center py-6 text-slate-400 text-xs">Loading…</td></tr>}
            {!isLoading && rows.length === 0 && <tr><td colSpan={3} className="text-center py-6 text-slate-400 text-xs">No containers attached yet.</td></tr>}
            {rows.map((r: any, idx: number) => (
              <tr key={`${r.cNo}-${idx}`} className="hover:bg-slate-50/60">
                <td className="px-3 py-2 font-mono font-bold text-slate-800">{r.cNo}</td>
                <td className="px-3 py-2 text-center"><span className={`pc-badge ${badge(r.status)}`}>{r.status || '—'}</span></td>
                <td className="px-3 py-2 text-right">
                  {isIn(r.status)
                    ? <span className="text-emerald-600 text-xs font-bold inline-flex items-center gap-1"><CheckCircle2 size={13} /> In Stock</span>
                    : <button onClick={() => gateIn(r.cNo)} disabled={busyCno === r.cNo} className="px-3 py-1 rounded-md text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60">{busyCno === r.cNo ? 'Gating…' : 'Gate In'}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ModalShell>
  );
}

// ── Document Vault upload ──────────────────────────────────────────────
// ── Logistics trio: RFQ · Attach · Dispatch (at "Manufacturing Complete") ─
//
// Three DISTINCT actions, exact replica of the reference:
//   • RFQ      → loadLogisticsRfqScreen  (broadcast shipment to freight+insurance)
//   • Attach   → openEarlyContainerModal (bind awarded vendor + container serials)
//   • Dispatch → openLogisticsModal/executeLogisticsDispatchBackend (final dispatch)

/** RFQ — "Broadcast Logistics RFQ": shipment params + select freight/insurance partners. */
export function BroadcastLogisticsRfqModal({ indentId, onClose }: ActionProps) {
  const { data: ind } = useQuery<{ indent: any }>({ queryKey: ['procurement', 'indent', indentId], queryFn: () => api.get(`/api/procurement/indents/${encodeURIComponent(indentId)}`) });
  const { data: fr } = useQuery<{ rows: any[] }>({ queryKey: ['procurement', 'masters', 'freight'], queryFn: () => api.get('/api/procurement/masters/freight') });
  const { data: insd } = useQuery<{ rows: any[] }>({ queryKey: ['procurement', 'masters', 'insurance'], queryFn: () => api.get('/api/procurement/masters/insurance') });
  const indent = ind?.indent;
  const freight = fr?.rows || [];
  const insurance = insd?.rows || [];
  const [pol, setPol] = useState('');
  const [pod, setPod] = useState('');
  const [sailDate, setSailDate] = useState('');
  const [cargoValue, setCargoValue] = useState('');
  const [freightIds, setFreightIds] = useState<string[]>([]);
  const [insuranceIds, setInsuranceIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const specs = indent ? `${indent.quantity || ''} × ${indent.sizeAllDetails || indent.itemType || ''}`.trim() : '';
  const toggle = (id: string, list: string[], set: (v: string[]) => void) => set(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const submit = async () => {
    const effectivePod = pod || indent?.deliveryLocation || '';
    if (!pol.trim() || !effectivePod.trim() || !sailDate || !cargoValue.trim()) {
      toast.error('Fill all shipment parameters (POL, POD, sail date, cargo value).'); return;
    }
    if (!freightIds.length && !insuranceIds.length) { toast.error('Select at least one partner to invite.'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ freightSent: number; insuranceSent: number; message: string; skippedNoEmail: string[] }>(`/api/procurement/logistics/${encodeURIComponent(indentId)}/rfq`, {
        pol, pod: effectivePod, sailDate, cargoValue: Number(cargoValue) || 0, freightIds, insuranceIds,
      });
      if ((r.freightSent + r.insuranceSent) === 0) {
        toast.error('No emails sent — the selected partners have no email on file. Add emails in Masters → Freight/Insurance.');
      } else {
        toast.success(r.message);
        if (r.skippedNoEmail?.length) toast(`No email on file (skipped): ${r.skippedNoEmail.join(', ')}`, { icon: '⚠️' });
        onClose();
      }
    } catch (e: any) { toast.error(e?.message || 'Broadcast failed'); } finally { setBusy(false); }
  };

  const partnerList = (rows: any[], ids: string[], set: (v: string[]) => void, empty: string) => (
    <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto divide-y divide-slate-100">
      {rows.map((v) => (
        <label key={v._id} className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-pointer hover:bg-slate-50">
          <input type="checkbox" checked={ids.includes(v._id)} onChange={() => toggle(v._id, ids, set)} />
          <span className="flex-1 min-w-0"><div className="font-medium text-slate-700 truncate">{v.name}</div><div className="text-[10px] text-slate-400 truncate">{v.email || '—'}</div></span>
        </label>
      ))}
      {!rows.length && <div className="p-3 text-[11px] text-slate-400 text-center">{empty}</div>}
    </div>
  );

  return (
    <ModalShell title="Broadcast Logistics RFQ" onClose={onClose} onSubmit={submit} busy={busy} submitLabel="Broadcast RFQ" submitDisabled={!freightIds.length && !insuranceIds.length} width="wide">
      <div className="text-[10px] uppercase tracking-wider text-teal-700 font-bold">1 · Shipment Parameters (sent to providers)</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Total Quantity & Specs"><div className="text-sm font-bold text-slate-700 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">{specs || '—'}</div></Field>
        <Field label="Target Sail Date"><input type="date" className="input" value={sailDate} onChange={(e) => setSailDate(e.target.value)} /></Field>
        <Field label="Origin Port (POL)"><input className="input" placeholder="e.g. Shanghai, China" value={pol} onChange={(e) => setPol(e.target.value)} /></Field>
        <Field label="Destination Port (POD)"><input className="input" value={pod || indent?.deliveryLocation || ''} onChange={(e) => setPod(e.target.value)} /></Field>
        <Field label="Est. Cargo Value (for insurance)"><input type="number" min={0} className="input" value={cargoValue} onChange={(e) => setCargoValue(e.target.value)} /></Field>
      </div>
      <div className="text-[10px] uppercase tracking-wider text-teal-700 font-bold mt-2">2 · Select Partners to Invite</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><div className="text-[11px] font-bold text-slate-600 mb-1">Freight Forwarders</div>{partnerList(freight, freightIds, setFreightIds, 'No freight forwarders — add them in Masters.')}</div>
        <div><div className="text-[11px] font-bold text-slate-600 mb-1">Insurance Providers</div>{partnerList(insurance, insuranceIds, setInsuranceIds, 'No insurance providers — add them in Masters.')}</div>
      </div>
    </ModalShell>
  );
}

/** Attach — "Attach Container Number": bind awarded vendor + container serials. */
export function AttachContainerModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const { data } = useQuery<{ indent: any; quotationItems?: any[] }>({ queryKey: ['procurement', 'indent', indentId], queryFn: () => api.get(`/api/procurement/indents/${encodeURIComponent(indentId)}`) });
  const indent = data?.indent;
  const awarded = (data?.quotationItems || []).filter((q: any) => q.isSelected);
  const [vendor, setVendor] = useState('');
  const [nums, setNums] = useState('');
  const [busy, setBusy] = useState(false);
  const vendorOptions = awarded.length
    ? awarded.map((q: any) => ({ value: q.vendorName, label: `${q.vendorName} (${q.awardedQty || q.quotedQty || 0} Units @ ${q.currency || ''} ${q.rate || 0} / YOM ${q.yom || ''})` }))
    : (indent?.awardedVendor ? [{ value: indent.awardedVendor, label: indent.awardedVendor }] : []);

  const submit = async () => {
    if (!nums.trim()) { toast.error('Enter at least one container number.'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ attached: number }>(`/api/procurement/logistics/${encodeURIComponent(indentId)}/attach-containers`, { vendorName: vendor || indent?.awardedVendor || '', containerNumbers: nums });
      toast.success(`${r.attached} container(s) locked to database`);
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    } catch (e: any) { toast.error(e?.message || 'Failed'); } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Attach Container Number" onClose={onClose} onSubmit={submit} busy={busy} submitLabel="Lock to Database">
      <Field label="Indent Ref">
        <div className="text-sm font-mono font-bold text-slate-700 px-3 py-2 bg-slate-50 rounded-lg border border-slate-200">{indentId}</div>
      </Field>
      <Field label="Select Awarded Vendor" required>
        <select className="input" value={vendor || indent?.awardedVendor || ''} onChange={(e) => setVendor(e.target.value)}>
          <option value="">— Select Vendor —</option>
          {vendorOptions.map((o: any) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </Field>
      <Field label="Container Numbers (one per line)" required>
        <textarea className="input min-h-[90px] font-mono" placeholder={'HLXU2234567\nMSCU7654321'} value={nums} onChange={(e) => setNums(e.target.value)} />
      </Field>
    </ModalShell>
  );
}

/** Dispatch — "Execute Logistics & Dispatch": freight + insurance + pickup + invoice → finalize. */
export function ExecuteLogisticsDispatchModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const { data: fr } = useQuery<{ rows: any[] }>({ queryKey: ['procurement', 'masters', 'freight'], queryFn: () => api.get('/api/procurement/masters/freight') });
  const { data: insd } = useQuery<{ rows: any[] }>({ queryKey: ['procurement', 'masters', 'insurance'], queryFn: () => api.get('/api/procurement/masters/insurance') });
  // Auto-fetch the attached container serials (reference vacuums them from the
  // Container_Repository) — the operator confirms, doesn't retype. Backend hard-stops if none.
  const { data: gateIn } = useQuery<{ data: any[] }>({ queryKey: ['procurement', 'gate-in', indentId], queryFn: () => api.get(`/api/procurement/containers/gate-in/${encodeURIComponent(indentId)}`) });
  const autoContainers = (gateIn?.data || []).map((c: any) => c.cNo).filter(Boolean).join('\n');
  const freight = fr?.rows || [];
  const insurance = insd?.rows || [];
  const [freightEmail, setFreightEmail] = useState('');
  const [insuranceEmail, setInsuranceEmail] = useState('');
  const [pickup, setPickup] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!freightEmail || !insuranceEmail || !pickup.trim() || !invoiceNo.trim() || !file) { toast.error('Fill all required fields and upload the Commercial Invoice.'); return; }
    setBusy(true);
    try {
      const up = await uploadFileToGridFs(file);
      await api.post(`/api/procurement/logistics/${encodeURIComponent(indentId)}/dispatch`, {
        freightEmail, insuranceEmail, pickupLocation: pickup, invoiceNo, invoiceFileId: up.id,
      });
      toast.success('Logistics dispatched · Accounts / Freight / Insurance notified');
      qc.invalidateQueries({ queryKey: ['procurement', 'import-queue'] });
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    } catch (e: any) { toast.error(e?.message || 'Dispatch failed'); } finally { setBusy(false); }
  };

  return (
    <ModalShell title="Execute Logistics & Dispatch" onClose={onClose} onSubmit={submit} busy={busy} submitLabel="Lock & Dispatch" width="wide">
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-[11px] text-emerald-800">
        Submitting this form will finalize the logistics move for this Logistics Desk and automatically notify Accounts, Freight, and Insurance.
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Freight Forwarder" required>
          <select className="input" value={freightEmail} onChange={(e) => setFreightEmail(e.target.value)}>
            <option value="">— Select Freight Forwarder —</option>
            {freight.map((v) => <option key={v._id} value={v.email}>{v.name}</option>)}
          </select>
        </Field>
        <Field label="Insurance Provider" required>
          <select className="input" value={insuranceEmail} onChange={(e) => setInsuranceEmail(e.target.value)}>
            <option value="">— Select Insurance Provider —</option>
            {insurance.map((v) => <option key={v._id} value={v.email}>{v.name}</option>)}
          </select>
        </Field>
        <Field label="Exact Pickup Location (Factory/Yard)" required>
          <input className="input" placeholder="e.g. Shanghai Port Yard A" value={pickup} onChange={(e) => setPickup(e.target.value)} />
        </Field>
        <Field label="Commercial Invoice No." required>
          <input className="input" placeholder="e.g. INV-2026-093" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} />
        </Field>
        <Field label="Commercial Invoice (.pdf)" required>
          <input type="file" accept=".pdf" className="input" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </Field>
      </div>
      <Field label="Container Number(s) — auto-fetched">
        <textarea className="input font-mono text-xs" rows={2} readOnly value={autoContainers} placeholder="No containers attached — use Attach Containers first" />
        {!autoContainers && <p className="text-[11px] text-red-500 mt-1">No containers attached. Attach container numbers before dispatch.</p>}
      </Field>
    </ModalShell>
  );
}

const DOC_TYPES = [
  'Proforma Invoice', 'Commercial Invoice', 'SIMS Certificate', 'CSC Certificate',
  'Commissionary Certificate', 'Test Certificate', 'Freight Invoice',
  'Insurance Invoice', 'Local Agent Invoice', 'Custom Duty Invoice',
  'Stamp Duty Invoice', 'CFS Invoice', 'CHA Invoice', 'Other',
];
export function DocVaultModal({ indentId, onClose }: ActionProps) {
  const qc = useQueryClient();
  const [docType, setDocType] = useState('Proforma Invoice');
  const [customLabel, setCustomLabel] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [hodEmail, setHodEmail] = useState('');
  const [busy, setBusy] = useState(false);

  // ── AI assist (advisory) ──────────────────────────────────────────────
  // When AI is enabled, picking a file auto-runs vision extraction and
  // pre-fills docType / custom label as an editable DRAFT. The user can
  // override everything; upload is never automatic.
  const aiEnabled = useAiEnabled();
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState<AiDocExtract | null>(null);
  const [aiWarnings, setAiWarnings] = useState<string[]>([]);
  const [aiTried, setAiTried] = useState(false);

  const runAiExtract = async (f: File) => {
    setAiBusy(true);
    setAiSuggestion(null);
    setAiWarnings([]);
    setAiTried(true);
    try {
      const dataUrl = await fileToDataUrl(f);
      const res = await aiDocExtract({ indentId, fileBase64: dataUrl });
      if (res.aiEnabled && res.suggestion) {
        const s = res.suggestion;
        setAiSuggestion(s);
        setAiWarnings(res.warnings || []);
        // Pre-select docType only when the model's value matches an option.
        if (s.docType && DOC_TYPES.includes(s.docType)) setDocType(s.docType);
        if (s.customLabel) setCustomLabel(s.customLabel);
      } else {
        setAiWarnings(res.warnings || []);
      }
    } catch {
      setAiSuggestion(null);
    } finally {
      setAiBusy(false);
    }
  };

  const onFileChange = (f: File | null) => {
    setFile(f);
    setAiSuggestion(null);
    setAiWarnings([]);
    setAiTried(false);
    if (f && aiEnabled) void runAiExtract(f);
  };

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const up = await uploadFileToGridFs(file);
      await api.post('/api/procurement/approvals/upload', {
        indentId,
        docType,
        customLabel,
        fileId: up.id,
        fileName: up.filename,
        contentType: up.contentType,
        hodEmail,
      });
      toast.success('Document submitted for HOD approval');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Upload to Document Vault" onClose={onClose} onSubmit={submit} busy={busy} submitDisabled={!file}>
      <Field label="Document type" required>
        <select className="input" value={docType} onChange={(e) => setDocType(e.target.value)}>
          {DOC_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
      {docType === 'Other' && (
        <Field label="Custom label"><input className="input" value={customLabel} onChange={(e) => setCustomLabel(e.target.value)} /></Field>
      )}
      <Field label="File" required>
        <input type="file" className="input" onChange={(e) => onFileChange(e.target.files?.[0] || null)} />
      </Field>

      {/* AI assist panel — advisory only, hidden entirely when AI is off */}
      {aiEnabled && file && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] space-y-1">
          {aiBusy ? (
            <div className="flex items-center gap-2 text-slate-600">
              <span className="inline-block h-3 w-3 rounded-full border-2 border-slate-300 border-t-teal-600 animate-spin" />
              Reading document…
            </div>
          ) : aiSuggestion ? (
            <>
              <div className="text-[10px] font-bold uppercase tracking-wider text-teal-700">AI read</div>
              <div className="text-slate-700">
                invoice <b>{aiSuggestion.invoiceNo || '—'}</b>
                {(aiSuggestion.amount || aiSuggestion.currency) ? (
                  <>, {aiSuggestion.amount || '—'} {aiSuggestion.currency || ''}</>
                ) : null}
                {aiSuggestion.blContainerNos?.length ? (
                  <>, BL containers: {aiSuggestion.blContainerNos.join(', ')}</>
                ) : null}
                {aiSuggestion.vessel ? <>, vessel {aiSuggestion.vessel}</> : null}
              </div>
              {aiWarnings.length > 0 && (
                <ul className="mt-1 space-y-0.5 text-amber-700">
                  {aiWarnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                </ul>
              )}
              <div className="text-[10px] text-slate-400">Fields pre-filled as a draft — review and edit before uploading.</div>
            </>
          ) : aiTried ? (
            <div className="text-slate-500">AI couldn’t read this — enter manually.</div>
          ) : null}
        </div>
      )}

      <Field label="HOD email (optional)"><input className="input" value={hodEmail} onChange={(e) => setHodEmail(e.target.value)} /></Field>
    </ModalShell>
  );
}

// ── Stage 2.1 — Domestic Depot & Surveyor Allocation ──────────────────
//
// Ports the legacy `openStage21Modal` + `executeStage21` UI. Captures
// the surveyor company, submission date, free-form remarks and (when
// available) the legacy depot + container assignment payload. POSTs to
// /api/procurement/indents/:id/stage21 which transitions the indent to
// "Stage 2.1 Completed" and unblocks Generate Draft PO.
export function Stage21Modal({
  indentId,
  onClose,
  indent,
}: ActionProps & { indent?: any }) {
  const qc = useQueryClient();
  const today = new Date().toISOString().slice(0, 10);

  const [surveyorCompany, setSurveyorCompany] = useState('');
  const [date, setDate] = useState(today);
  const [remarks, setRemarks] = useState('');
  // Optional legacy-parity fields — collapsible advanced section.
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [depotName, setDepotName] = useState('');
  const [depotAddress, setDepotAddress] = useState('');
  const [surveyorName, setSurveyorName] = useState('');
  const [surveyNo, setSurveyNo] = useState('');
  const [containerNumbers, setContainerNumbers] = useState('');

  const submit = useMutation({
    mutationFn: () => api.post(`/api/procurement/indents/${encodeURIComponent(indentId)}/stage21`, {
      surveyorCompany, date, remarks,
      depotName, depotAddress, surveyorName, surveyNo, containerNumbers,
    }),
    onSuccess: () => {
      toast.success('Container details sent for inspection · ready to generate PO');
      qc.invalidateQueries({ queryKey: ['procurement', 'indent', indentId] });
      qc.invalidateQueries({ queryKey: ['procurement', 'domestic-queue'] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.message || 'Stage 2.1 submission failed'),
  });

  return (
    <ModalShell
      title="Stage 2.1 · Send container details for inspection"
      onClose={onClose}
      onSubmit={() => submit.mutate()}
      busy={submit.isPending}
      submitLabel="Send for inspection"
      submitDisabled={!surveyorCompany.trim() || !date.trim()}
      width="wide"
    >
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-[11px] text-slate-700">
        Sends the container details to the depot and surveyor so the inspection
        can be booked against the awarded vendor. On submit the indent moves to
        <b> Stage 2.1 Completed</b> and the desk can generate the Draft PO.
        {indent?.awardedVendor && (
          <div className="mt-1 text-[10px] uppercase tracking-wider font-bold text-slate-500">
            Awarded vendor: <span className="text-slate-800">{indent.awardedVendor}</span>
            {indent?.quantity ? <> · Qty <span className="text-slate-800">{indent.quantity}</span></> : null}
          </div>
        )}
      </div>

      <Field label="Surveyor company" required>
        <input
          className="input"
          value={surveyorCompany}
          onChange={(e) => setSurveyorCompany(e.target.value)}
          placeholder="e.g., Bureau Veritas · SGS · Intertek"
        />
      </Field>

      <Field label="Submission date" required>
        <input type="date" className="input" value={date} onChange={(e) => setDate(e.target.value)} />
      </Field>

      <Field label="Remarks">
        <textarea
          className="input min-h-[80px]"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          placeholder="Any handover notes for inspection / surveyor (optional)"
        />
      </Field>

      <button
        type="button"
        onClick={() => setShowAdvanced((s) => !s)}
        className="text-[11px] font-bold text-teal-700 hover:underline"
      >
        {showAdvanced ? '− Hide depot / container details' : '+ Add depot / container details (optional)'}
      </button>

      {showAdvanced && (
        <div className="space-y-3 border-l-2 border-slate-200 pl-3 ml-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Depot name">
              <input className="input" value={depotName} onChange={(e) => setDepotName(e.target.value)} placeholder="e.g., Eng Kong" />
            </Field>
            <Field label="Depot address">
              <input className="input" value={depotAddress} onChange={(e) => setDepotAddress(e.target.value)} />
            </Field>
            <Field label="Surveyor name">
              <input className="input" value={surveyorName} onChange={(e) => setSurveyorName(e.target.value)} placeholder="Individual surveyor at the depot" />
            </Field>
            <Field label="Survey no.">
              <input className="input" value={surveyNo} onChange={(e) => setSurveyNo(e.target.value)} />
            </Field>
          </div>
          <Field label="Container numbers (comma- or newline-separated)">
            <textarea
              className="input min-h-[80px] font-mono text-[12px]"
              value={containerNumbers}
              onChange={(e) => setContainerNumbers(e.target.value)}
              placeholder="SZLU9901527,&#10;SZLU9901933"
            />
          </Field>
        </div>
      )}
    </ModalShell>
  );
}

// ── Shared Field wrapper ───────────────────────────────────────────────
function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
